/**
 * InsightCard — the Quick Study result card and its pure helpers.
 *
 * Extracted from `pages/QuickStudyPage/MessageRow.tsx`, which mixed the chat-message
 * shell (a page concern, retired with the Quick Study surface) with the result renderer
 * (reused by the thread assistant, the report views and the public share route). Only the
 * renderer half lives here: the card itself, its segment/quote helpers, the plot and metric
 * labels, the combo keys, the coverage footer and the next-step chip helpers.
 *
 * Nothing in this file reaches back into `pages/QuickStudyPage/`. Wire shapes come from
 * `../quickStudyTypes`; `MessageRow` re-exports the names below so its own callers are
 * unchanged.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { AlertTriangle, Info, X } from 'lucide-react'
import { AssistantActionCard } from '@/components/chat/AssistantActionCard'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { SubsegmentChip } from '@/components/twin-chat'
import { cn } from '@/lib/utils'
import { PlotRenderer, cutsDrawStampedStats, monadicCoverageNote, routesToOpenEnd, resolveNpsAwarePlot } from '../PlotRenderer'
import { PlotCard } from '../PlotCard'
import { CompareSegmentsPicker } from '../CompareSegmentsPicker'
import { ExhibitViewSettings } from './ExhibitViewSettings'
import { type DivergingChromeData } from '../DivergingStackedRating'
import { buildDemographicCutGroups, maxCutsFor } from '../divergingStacked'
import { supportsSegments } from '../segmentBreakdown'
import { COMBO_WHY_FIELD, comboScaleMax } from '../comboAnswers'
import { BandCaveatSuppressedContext } from '../segmentedToggle'
import { bandThemesToHeatmap } from '../openEndThemeGroups'
import { qualifiedCutDisplayLabel } from '../audience-segments'
import { TwinsMarkLoader } from '../TwinsMarkLoader'
import { buildCardFootnote } from '../QsCardChrome'
import { ComparisonLabelText } from '../ComparisonLabelText'
import { QuickStudyOpenEndCard, buildQuoteItems, availableOpenEndTabs, OpenEndTab } from '../QuickStudyOpenEndCard'
import { availableMonadicTabs, GroupedThemeBar, buildVariantColors } from '../MonadicOpenEndCard'
import { deriveThemeColumns } from '../MonadicThemeHeatmap'
import { getAppChartPalette } from '@/lib/charts/chartjs'
import { AttributedQuoteCard } from '../AttributedQuoteCard/AttributedQuoteCard'
import { NarrativeQuotePlotCard, narrativeQuotesFromPlot, readQuotePlot, readThemeHeatPlot } from '../OpenEndPlots'
import { TakeawayBlock } from '../TakeawayBlock/TakeawayBlock'
import { buildSegmentCandidates, buildAuthoredSegmentGroups, cutIdsForAttribute, sliceResponsesByCutIds, lowerSegmentRefs, cutIdDisplayLabel, MIN_SEGMENT_N, lowVarianceHint, ranTextOnly, nextStepsIntro, buildNextSteps, SHOW_QS_NEXT_STEPS, focusGroupTopic, canTopUp, DEFAULT_ENOUGH, type SegmentQuestion, type NextStepItem, splitCritiqueLead, collectComparisonLabels, emphasizeComparisonLabels, honestyThreadsSuffix, isHeadlineRestatement, isLowSampleCard, canRetrofit, buildMonadicVersionLabelMap } from '../quickStudyPure'
import { isSplitResult, topTwoOptions } from '../quickStudyConverse'
import { twinDisplayName, twinIdentityTagline, twinInitials, twinSubsegmentLabels, type QuickStudyPersona } from '../QuickStudyTwinsPanel'
import { plotQuestionHeader, plotQuestionScale } from '../plotQuestionHeader'
import { stampedStatChoice } from '../quickStudyStatViews'
import { hasQuickStudyScorecardPlot, hasQuickStudyScorecardRadar } from '../QuickStudyGridPlot'
import {
  findLiftPairOnResult,
  liftQuestionIdsOf,
  compiledGridRowCount,
  readLiftScorecardForPair,
  readLiftTagsFromQuestion,
} from '@/features/studies/questionnaire/assets/liftTags'
import { formatLiftTriplet } from '@/features/studies/report/page/components/tabs/formatAbsoluteScore'
import type { Result, GenerationSpec, ExhibitSpec } from '../quickStudyTypes'

// QS-32 — human labels for the per-insight chart-type switcher (driven by the
// backend's `allowed_plots`, which already encodes the valid representations per data shape).
// QS-77: each label must name what its renderer ACTUALLY draws (see PlotRenderer):
//   'rating' → NumericChart, a single AVERAGE bar (NOT a distribution) → "Average".
//   'bar'    → SingleChoiceChart, the per-value DISTRIBUTION → "Distribution".
//   'grouped_bar' → the NPS promoter/passive/detractor BUCKET bar → "NPS buckets" (only
//     ever offered on a true 0–10 NPS rating now; allowed_plots gates it off 1–5 ratings).
export const PLOT_LABELS: Record<string, string> = {
  bar: 'Distribution', pie: 'Pie', grouped_bar: 'NPS buckets', ranking: 'Ranking',
  rating: 'Average', histogram: 'Histogram', radar: 'Radar',
  heatmap: 'Heatmap', quadrant: 'Quadrant', matrix: 'Matrix', text_responses: 'Themes',
  // NPS (0–10 rating): a diverging bar (detractors left / promoters right) is the
  // canonical NPS viz — offered alongside the grouped bar via allowed_plots.
  nps_diverging: 'NPS',
  // Van Westendorp price sensitivity — the four-curve meter (PSMChart).
  psm: 'Price sensitivity',
  migration_matrix: 'Migration read',
}


// QS — the render-only METRIC switcher options (mirrors the "View as" chart-type
// switcher). Only offered when the result's `metric` is a numeric scorecard read
// ('mean' | 'top_2_box'); never for '' (categorical/open) or 'nps' (its own viz).
//   'mean'       → per-row average rating (the score axis).
//   'top_2_box'  → per-row % in the top two boxes (the intent / likelihood read, 0–100%).
export const METRIC_LABELS: Record<string, string> = {
  mean: 'Average',
  top_2_box: 'Top-2-box %',
}
const METRIC_OPTS: Array<'mean' | 'top_2_box'> = ['mean', 'top_2_box']

// The open-end plot ids (themes / verbatims / word cloud). These are routed through
// `renderOpenEndInsights` and never get the (generic) segment picker. Since Step 6 they DO
// adopt `PlotCard` on the chat surface (the themes bars are the captured exhibit; the
// Themes|Quotes toggle lifts into the PlotCard header), so this set no longer gates PlotCard
// exclusion — it now marks OE-specific behavior: which cards get the lifted view toggle, the
// themes-only export gate, and (Step 7) the quotes-view segment picker. Hoisted to a single
// source of truth so the segment gate, the monadic-open-end guard, and the OE gates all test
// the SAME set (no drifting inline literals).
const OPEN_END_PLOTS = new Set<string>(['text_responses', 'open_end', 'themes', 'word_cloud', 'wordcloud'])
function isOpenEndPlotId(plot: string): boolean {
  return OPEN_END_PLOTS.has(plot)
}


// Coverage footer — a quiet, ONE-LINE base-size note (R10 grammar: "Base: …"). Full
// coverage reads as a plain count ("Base: 40 twins"); a partial set keeps the
// "answered" phrasing so it doesn't read like a filter/failure. Intentionally free of
// any "synthetic / directional / not projectable" framing — we state the sample, not
// a disclaimer. This is the FALLBACK footer for cards with no chrome of their own
// (NPS/PSM/bar/pie/radar/…). On the chat surface a converged diverging/scorecard card gets
// this same base-size line via its exhibit-published footnote, and the monadic open-end card
// carries its methodology note (`monadicCoverageNote`) instead (see the `plotCardFootnote`
// branch in InsightCard) — neither stacks a second base-size sentence under the first.
export function coverageFooter(
  n: number,
  total: number,
  m?: {
    monadic?: boolean
    n_per_creative?: number
    monadic_item_count?: number
    item_noun?: string
    n_unique_respondents?: number
    n_reused_threads?: number
  },
): string {
  // QSB-8: a monadic run mixes units (n = twins for open-ends, n = reads for scalars;
  // total = reads) — "100 of 300 twins answered" implied 200 non-responders and
  // "Based on 300 twins" tripled the real panel. State both units explicitly.
  if (m?.monadic && m.n_per_creative && m.monadic_item_count) {
    const noun = (m.item_noun || 'creative').trim() || 'creative'
    const count = m.monadic_item_count
    const reads = m.n_per_creative * count
    // True-monadic Step 5 — honesty suffix: '' unless a twin-monadic reuse fallback fired.
    return buildCardFootnote(
      `${m.n_per_creative} twins × ${count} ${noun}${count === 1 ? '' : 's'} (${reads} isolated reads)${honestyThreadsSuffix(m.n_unique_respondents, m.n_reused_threads)}`,
    )
  }
  // W0.5 — a monadic result missing the item count must still never read as a pooled base:
  // "Base: 200 twins" under per-version exhibits overstates each version's n. Phrase per version.
  if (m?.monadic && m.n_per_creative) {
    const noun = (m.item_noun || 'creative').trim() || 'creative'
    return buildCardFootnote(
      `${m.n_per_creative} twins per ${noun}${honestyThreadsSuffix(m.n_unique_respondents, m.n_reused_threads)}`,
    )
  }
  if (total && n < total) return buildCardFootnote(`${n} of ${total} twins answered`)
  return buildCardFootnote(`${n} twins`)
}

// Combo quant+OE (Step G) — the tab strip on a combo card: the default quant Chart, the paired
// open-end (themes + band split), and the whys with score badges.
export type ComboTab = 'chart' | 'why' | 'quotes'

// The survey_data key carrying each twin's paired why-verbatim (Critical Decisions: combo =
// `{qs_main: score, qs_main_why: prose}` on ONE twin's survey_data). Distinct from the quant
// answer key (the question's own name), so the score badge and the why never collide.
// Re-exported from the ledger, which needs the same key to mint report quotes: two copies of the
// string is how the chat card and the report drifted onto different fields in the first place.
export const COMBO_WHY_KEY = COMBO_WHY_FIELD

// Combo (Wave-3 Step E) — the ANCHORED-grid provenance footnote. A weakest-dimension grid combo
// (a Tier-2 "drivers"/"gap" OE on a 2+-dimension matrixdropdown) asks a DIFFERENT literal question
// per twin ("you rated X lowest — what drove that?"), so the single-`{stem}` "Scored first, then
// asked: …" template would misreport what was actually asked. This CONSTANT mirrors the server
// source of truth (arc_registry.PAIRED_OE_ANCHORED_GRID_FOOTNOTE) — it DESCRIBES the anchoring rule
// instead of echoing one twin's question. Promotes to a wire field if the two ever diverge.
export const COMBO_ANCHORED_GRID_FOOTNOTE =
  'Each twin scored every dimension, then explained their lowest.'

// Combo (Step G) — the rating-scale ceiling for the per-quote score badge ("score/scaleMax").
// Re-exported from the ledger, which needs the same scale to label a report quote's answer chip:
// two readers would let the chat badge and the report chip disagree about the same twin's score.
export const comboScaleMaxFromQuestion = comboScaleMax

// Combo (Step G) — a twin's paired why-verbatim off its `survey_data` (the uncurated source of
// truth; strings only — the flatten/skip-empty mirror of the OE card's own extractor).
export function comboWhyOf(response: { survey_data?: Record<string, unknown> } | null | undefined): string {
  const v = response?.survey_data?.[COMBO_WHY_KEY]
  if (v == null) return ''
  if (Array.isArray(v)) return v.map((x) => String(x)).join(' ').trim()
  if (typeof v === 'object') return ''
  return String(v).trim()
}

// WS-H (LP P3) — the residual product echo of the capture off-by-one: a SETTLED insight
// panel whose responses belong to a PREVIOUS stage (e.g. on re-query / reload before the
// new responses land). The Wave-1 loading mask only covers the in-flight `querying` case;
// this catches the settled-but-stale case so a previous stage's series never renders under
// a freshly-built question. Survey-agnostic: a non-stale result's responses answer its OWN
// question, i.e. carry the question's `name` key in survey_data (the exact key PlotRenderer
// reads). Keyed only on the result's own question NAME — never on any value, arc, or stage.
// Monadic results combine per-creative passes whose survey_data is keyed per pass, so they
// legitimately don't carry the top-level question name and are EXEMPT.
export function insightResponsesStale(r: {
  question?: { name?: string } | null
  responses?: Array<{ survey_data?: Record<string, unknown> }> | null
  monadic?: boolean
}): boolean {
  const name = (r.question?.name || '').trim()
  const responses = r.responses ?? []
  if (!name || responses.length === 0 || r.monadic) return false
  return !responses.some((resp) => resp?.survey_data != null && name in resp.survey_data)
}

// QS-41/45 — cap a focus group so the seeded room stays legible (mirrors QuickStudyTwinsPanel).
// Value-imported by QuickStudyPage so the page and InsightCard slice against the same const.
export const CONVERSE_GROUP_CAP = 10


// Competitor-style "Key Segment Insights" section (subsegment-note redesign): an
// opinionated mini-headline above the beat's own paragraph, with the deterministic
// thin-read / sub-floor / all-empty honesty note surfaced as a quiet tooltip icon on the
// heading instead of folded into the prose. Shared by the structured-narrative branch and
// the standalone (bullets + critique both empty) fallback branch below so the heading/
// tooltip idiom never drifts between the two. Renders nothing when there's neither a beat
// nor a caveat to show (never an empty heading).
function SegmentNoteSection({
  segmentNote,
  segmentNoteTitle,
  segmentNoteCaveat,
  comparisonLabels,
}: {
  segmentNote?: string | null
  segmentNoteTitle?: string | null
  segmentNoteCaveat?: string | null
  comparisonLabels: string[]
}) {
  if (!segmentNote && !segmentNoteCaveat) return null
  return (
    // Vertical padding on the wrapper (not `mb-*`/`mt-*` on the heading child, per the
    // gap-on-parents rule) sets the segment sub-read apart as its own section header:
    // generous top padding separates it from the prior section, and a comfortable gap to its
    // own body below — a heading deserves room, not a cramped single line. The heading font
    // matches the body copy (size="base") so it reads as a peer, distinguished by weight only.
    <div className="space-y-2 pt-6">
      <Heading
        level={4}
        size="base"
        weight="medium"
        className="font-sans leading-tight text-twin-chat-fg"
        data-testid="qs-segment-note-title"
      >
        Segment read: {segmentNoteTitle || 'Key segment insights'}
        {/* Thin-read/sub-floor/all-empty caveat — a quiet inline glyph right after the
            heading text, full note on hover/focus via tooltip. Mirrors the grounding-
            withheld icon idiom above exactly (same classes/structure) so the two subtle
            honesty affordances read as one visual language across the card. */}
        {segmentNoteCaveat && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Subsegment note: ${segmentNoteCaveat}`}
                  data-testid="qs-segment-note-caveat"
                  className="ml-1.5 inline-flex align-middle text-chart-warning/80 transition-colors hover:text-chart-warning focus-visible:text-chart-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-xs">
                {segmentNoteCaveat}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Heading>
      {segmentNote && (
        // The segment sub-read renders as bullet(s) too, matching the main narrative's evidence
        // list — same dot / indent / base type — rather than a lone paragraph under the heading.
        <ul className="space-y-1.5" data-testid="qs-narrative-segment-note">
          <li className="flex gap-2">
            <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-twin-chat-fg-muted" />
            <Text as="span" size="base" className="leading-relaxed text-twin-chat-fg">
              <ComparisonLabelText text={segmentNote} labels={comparisonLabels} />
            </Text>
          </li>
        </ul>
      )}
    </div>
  )
}

// Attribute the narrative pull-quote (`woven_quote` — a bare verbatim string the backend sends
// with NO speaker) to the twin who said it, by matching its text against the sampled responses
// through the SAME `buildQuoteItems` pipeline the open-end Quotes gallery uses (name / blurb /
// segment chip, with honest attribute fallbacks — never invented). Returns null when the quote
// doesn't correspond to any sampled response (e.g. an LLM paraphrase) → the quote renders alone.
function normalizeQuoteText(s: string): string {
  return (s || '')
    .replace(/[“”‘’]/g, '"')
    .replace(/^[\s"']+|[\s"']+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function findWovenQuoteAttribution(
  items: ReturnType<typeof buildQuoteItems>,
  wovenQuote: string,
): ReturnType<typeof buildQuoteItems>[number] | null {
  const q = normalizeQuoteText(wovenQuote)
  if (!q) return null
  // Exact match first; then containment either direction (the LLM may trim the verbatim's edges).
  const exact = items.find((it) => normalizeQuoteText(it.text) === q)
  if (exact) return exact
  const loose = items.find((it) => {
    const t = normalizeQuoteText(it.text)
    return t.length > 0 && (t.includes(q) || q.includes(t))
  })
  return loose ?? null
}

// Prose-narrative migration (Step 2) — the writer's per-paragraph testimonials, unlike the
// legacy `woven_quote` above, arrive with a `persona_id` STAMPED server-side by the grounding
// guard (never authored/guessed by the LLM). Attribution is therefore a strict id lookup
// against the fetched `personas` list — no fuzzy text-match fallback. `NarrativeQuote` mirrors
// python's `NarrativeQuote` (models.py) 1:1, snake_case wire fields, no aliasing.
type NarrativeQuote = NonNullable<Result['quotes']>[number]

interface NarrativeQuoteAttribution {
  name: string
  blurb: string
  segmentLabel: string
  // Step 4b (AttributedQuoteCard adoption): the framed quote card needs an avatar, so
  // attribution now also carries the initials fallback + portrait.
  initials: string
  imageUrl?: string
}

// Resolve one narrative quote's twin STRICTLY off `quote.persona_id`. Returns null when the
// quote carries no id, or the id doesn't match a fetched persona — either way there is no
// honest attribution to show, and the caller must DROP the quote rather than render it bare.
function resolveNarrativeQuoteAttribution(
  quote: NarrativeQuote,
  personas: QuickStudyPersona[],
  responses: Result['responses'],
): NarrativeQuoteAttribution | null {
  const personaId = (quote.persona_id || '').trim()
  if (!personaId) return null
  const persona = personas.find((p) => p.personaId === personaId)
  if (!persona) return null
  return {
    name: twinDisplayName(persona),
    blurb: twinIdentityTagline(persona),
    segmentLabel: twinSubsegmentLabels(persona, responses)[0] || '',
    initials: twinInitials(persona),
    imageUrl: persona.imageUrl || undefined,
  }
}

// Exported for focused render tests (the consolidated next-steps list + in-card
// selection). With an empty `responses` result the chart short-circuits, so the test
// mounts only the prose + next-steps block — no canvas in jsdom.
export function InsightCard({
  result: r,
  onAskNext,
  onSelectStep,
  selectedStepKey,
  onDebate,
  onConverse,
  onConfirmMore,
  confirmMoreCap,
  confirmMoreBusy,
  answered = 1,
  enough = DEFAULT_ENOUGH,
  advanceConsumed = false,
  plotOverride: plotOverrideProp,
  onPlotOverride,
  metricOverride: metricOverrideProp,
  onMetricOverride,
  selectedCuts: selectedCutsProp,
  onSelectedCuts,
  exhibitSpec: exhibitSpecProp,
  personas,
  populationId,
  studyHadCreative,
  onPrefill,
  askedStageIds,
  querying = false,
  audienceType,
  bubbled = false,
  chrome = 'inline',
  region = 'all',
  pairedOeStem,
  onRetrofit,
  retrofitting = false,
}: {
  result: Result
  onAskNext?: (text: string, opts?: { fromSuggestion?: boolean; suggestionKind?: string }) => void
  // The consolidated next-steps list reflects the chosen move in-card: `selectedStepKey`
  // is the currently-selected step (persisted on the message), `onSelectStep` records a click.
  onSelectStep?: (key: string) => void
  selectedStepKey?: string
  onDebate?: () => void
  onConverse?: () => void
  // ★ Stimulus grounding — did the STUDY have a creative attached when this result ran?
  // Drives the "ran text-only" badge (true + no stimulus image on this result ⇒ flag).
  studyHadCreative?: boolean
  // P1 — "Run more twins to confirm": present only when the page passes a handler (i.e.
  // this insight has a stored spec) AND the result is flagged too-close AND there's
  // headroom under the cap. The handler re-issues the same generation at a larger sample.
  onConfirmMore?: () => void
  confirmMoreCap?: number
  confirmMoreBusy?: boolean
  // Arc-maturity signals for the depth-aware focus-group recommendation.
  answered?: number
  enough?: number
  advanceConsumed?: boolean
  // QS-32b — the chosen alternate plot is owned by the page (keyed by insight id) so
  // the report mirrors it. When the page doesn't supply these (standalone), the card
  // falls back to local state below — default (backend plot) behavior is unchanged.
  plotOverride?: string
  onPlotOverride?: (plot: string) => void
  // R4.4 — same page-level channel as plotOverride. Absent → local metric state.
  metricOverride?: string
  onMetricOverride?: (metric: string) => void
  // R4.4 — controlled-or-local cuts. A streamed exhibit spec resyncs local state;
  // the no-spec path stays the one-shot segment_by seed.
  selectedCuts?: Array<string>
  onSelectedCuts?: (ids: Array<string>) => void
  // Structure-only exhibit. Absent → today's rendering (byte-identical path).
  exhibitSpec?: ExhibitSpec
  // The run's sampled twins, so the open-end Quotes view can attribute each verbatim
  // back to a name/blurb/portrait (passed straight through to QuickStudyOpenEndCard).
  personas?: QuickStudyPersona[]
  // Population/audience id the run's twins belong to. Passed straight through to
  // QuickStudyOpenEndCard (and the nested PlotRenderer) so a Quotes-view quote still
  // missing an image after persona/attribute resolution can ask the slim picker view
  // instead of staying on initials. Optional — omitted preserves today's behavior.
  populationId?: string
  // "See all questions in this template" navigator: `onPrefill` drops a chosen question's ask
  // into the composer (the user tweaks + sends); `askedStageIds` marks the stages already run.
  // When `onPrefill` is absent (standalone/tests) the navigator is hidden.
  onPrefill?: (text: string) => void
  askedStageIds?: Set<string>
  // WS-1 grounding (presentational): when this stage's own result is NOT yet settled (a run
  // is in flight for THIS card), gate the chart + Themes/Quotes/Grid behind a loading mask so
  // the prior stage's series is never shown under a still-querying question. Defaults false —
  // a settled result renders exactly as before.
  querying?: boolean
  // F1a — audience B2B/B2C signal ('b2b' | 'b2c' | undefined). On B2B the "Break down by"
  // dropdown drops consumer-demographic auto-cuts (age/gender/…); firmographic cuts survive.
  audienceType?: string
  // Chat presentation: when true, the card renders as TWO stacked assistant bubbles — the
  // insight/results in the first, the "what to ask next" moves (next steps + template navigator)
  // in a second bubble — so they read as two separate assistant messages. Default false keeps the
  // single-block layout used by the report, the card-preview page, and focused tests.
  bubbled?: boolean
  // Plot-region shell, independent of bubble layout. `'plot-card'` adopts the shared PlotCard
  // chrome (thread twin-test results) without enabling OE segment widening, quotes slicing, or
  // AssistantActionCard bubbles — those stay gated on `bubbled` alone. Default `'inline'` keeps
  // report/preview/tests unchanged when `bubbled` is also false.
  chrome?: 'inline' | 'plot-card'
  // Thread report layout: render the written finding, the plot exhibit, or both.
  // Default `'all'` keeps Quick Study / report / tests as one card. Thread results
  // split the two so the cut picker can sit with plots only.
  region?: 'all' | 'narrative' | 'plot'
  // Combo quant+OE retrofit CTA (Step H, Part 4 / Step I wiring) — "Ask the follow-up" on a
  // NON-combo quant card. `pairedOeStem` is the message spec's agent-stamped follow-up stem
  // (undefined/blank ⇒ this stage has no pairing ⇒ nothing renders, regardless of eligibility).
  // `onRetrofit` fires the follow-up-only run (absent ⇒ hidden, mirrors every other card action).
  // `retrofitting` is true for the duration of that run ⇒ the CTA shows a busy/spinner state and
  // is disabled. Eligibility itself (canRetrofit) is derived in-card from `result`, never passed in.
  pairedOeStem?: string
  onRetrofit?: () => void
  retrofitting?: boolean
}) {
  const { t } = useTranslation('app')
  const responses = useMemo(() => r.responses ?? [], [r.responses])
  // R4.4 — prop wins; composer may stamp the same shape on the Result so StudyDoc
  // embeds pick it up without a new ThreadStudyDocParts field.
  const exhibitSpec = exhibitSpecProp ?? r.exhibitSpec
  const liftPair = useMemo(() => findLiftPairOnResult(r), [r])
  const [prePostHalf, setPrePostHalf] = useState<'pre' | 'post'>(
    () => readLiftTagsFromQuestion(r.question)?.half ?? 'pre',
  )
  // Combo quant+OE (Step G) — a combo result is a QUANT card that ALSO carries a paired open-end
  // (each twin's score AND their why). Branch on the PRESENCE of `paired_oe_kind`, never on the
  // question type / the registry: the field is absent on every non-combo result and present only
  // when ≥1 twin returned a why (Step F). Defined early so the woven-quote attribution below and
  // the render/tab logic further down all read the same flag.
  const isComboCard = Boolean(r.paired_oe_kind)
  // W0.7 / thread twin-test: version chips follow the id→label map, never combo-vs-quant.
  // Ranking / options-are-stimuli feedback is often NOT `r.monadic` but still carries
  // version ids on `option_metadata` / choices. undefined when no source resolves → no chips.
  const versionLabelById = useMemo(() => buildMonadicVersionLabelMap(r), [r])
  // Narrative pull-quote attribution — the twin behind `woven_quote` (name / blurb / segment
  // chip), resolved from the sampled responses. null ⇒ unmatched ⇒ the quote renders alone.
  // For a combo the woven_quote is a WHY, so match against the `qs_main_why` verbatims (not the
  // quant answer) so the attribution resolves.
  const wovenQuoteAttribution = useMemo(() => {
    const wq = (r.woven_quote || '').trim()
    if (!wq || responses.length === 0) return null
    return findWovenQuoteAttribution(
      buildQuoteItems(responses, r.question, personas ?? [], {
        ...(isComboCard ? { whyKey: COMBO_WHY_KEY } : {}),
        versionLabelById,
      }),
      wq,
    )
  }, [r.woven_quote, r.question, responses, personas, isComboCard, versionLabelById])
  const segmentCandidates = useMemo(() => buildSegmentCandidates(responses, audienceType), [responses, audienceType])
  // Wave 4 — the diverging/scorecard exhibits that render their OWN Compare-segments picker
  // (DivergingStackedRating / ScorecardHeatmap: monadic rating/scorecard or a scalar rating).
  // On the chat surface they converge into `PlotCard`: the exhibit renders CHROME-LESS and
  // publishes its picker/subtitle/footnote UP (see `publishedChrome`), so the ONE header picker
  // is the exhibit's own — driven by its cut state (attributability gate, per-item cut model
  // and readability cap all stay inside the exhibit). They therefore stay OUT of the generic
  // `segmentable` set below (that would render InsightCard's inline picker too → a double
  // picker). The defensive matrix/numeric `diverging_stacked` fallback (no monadic series, not
  // a plain rating) has no internal picker, so it is NOT in this set and DOES become segmentable
  // through the generic seam — the effective "drop the `diverging_stacked` term" from Step D.
  const exhibitPlot = resolveNpsAwarePlot(r.plot, r.metric || '')
  const isConvergedDiverging =
    exhibitPlot === 'diverging_stacked' &&
    (Boolean(r.monadic && ((r.monadic_scorecard?.length ?? 0) > 0 || (r.monadic_rating?.length ?? 0) > 0)) ||
      r.question?.type === 'rating')
  // Segment split applies to every response-derived chart that can be sliced client-side:
  // `supportsSegments` already excludes the pricing shapes (psm / migration_matrix — they
  // render backend aggregates and get the "not available yet" note, not a picker). We add two
  // exclusions on top: open-end plots (themes/verbatims/word cloud — routed through
  // `renderOpenEndInsights`, no picker) and the converged diverging/scorecard exhibits (they
  // drive their own header picker through the published channel — see above).
  // Evaluated on `r.plot` (the backend plot), matching the prior whitelist's semantics — the
  // render-only View-as override does not change whether the picker is offered.
  const segmentable = supportsSegments(r.plot) && !isOpenEndPlotId(r.plot) && !isConvergedDiverging
  // The ONE segment control is now the multi-select Compare-segments picker (replacing the
  // single-select "Break down by X" dropdown): authored "Audience segments" prepend the
  // derived demographic cut groups — same `{ groupLabel, options }` shape, no API change.
  // Step 7 (review F1) — the cut groups feed BOTH the generic quant picker (`segmentable`) and the
  // plain-OE QUOTES-view picker. `segmentable` is FALSE for every OE plot, so widen the build gate
  // to also cover a bubbled open-end card with responses, or the OE quotes picker could never
  // render. `selectedCuts` still inits to `[]` for OE (no `segment_by` auto-seed on quotes).
  const cutGroups = useMemo(
    () =>
      segmentable || (bubbled && isOpenEndPlotId(r.plot) && responses.length > 0)
        ? [
            ...buildAuthoredSegmentGroups(r.segment_options, { includeEmpty: Boolean(exhibitSpec) }),
            ...buildDemographicCutGroups(responses, audienceType),
          ]
        : [],
    [segmentable, bubbled, r.plot, r.segment_options, responses, audienceType, exhibitSpec],
  )
  const loweredExhibitCuts = useMemo(
    () =>
      exhibitSpec
        ? lowerSegmentRefs(exhibitSpec.preAppliedCuts ?? [], {
            segmentOptions: r.segment_options,
            responses,
          })
        : [],
    [exhibitSpec, r.segment_options, responses],
  )
  const specCutIds = useMemo(
    () => loweredExhibitCuts.filter((c) => !c.emptyBase).map((c) => c.cutId),
    [loweredExhibitCuts],
  )
  const emptyBaseCuts = useMemo(
    () => loweredExhibitCuts.filter((c) => c.emptyBase),
    [loweredExhibitCuts],
  )
  const specCutsKey = specCutIds.join('\0')
  // Honor the agent's requested cut: if it asked to "break down by gender", default the
  // picker to that attribute's cuts (all its values) instead of making the marketer pick.
  // R4.4 — controlled-or-local. No-spec stays the one-shot segment_by seed (no resync).
  const [localSelectedCuts, setLocalSelectedCuts] = useState<Array<string>>(() =>
    exhibitSpec
      ? specCutIds
      : segmentable
        ? cutIdsForAttribute(cutGroups, matchSegmentName(r.segment_by, segmentCandidates))
        : [],
  )
  const [appliedSpecCutsKey, setAppliedSpecCutsKey] = useState(exhibitSpec ? specCutsKey : '')
  useEffect(() => {
    if (!exhibitSpec) return
    if (appliedSpecCutsKey === specCutsKey) return
    if (!onSelectedCuts) {
      setLocalSelectedCuts(specCutIds)
    }
    setAppliedSpecCutsKey(specCutsKey)
  }, [exhibitSpec, specCutsKey, specCutIds, appliedSpecCutsKey, onSelectedCuts])
  const selectedCuts = onSelectedCuts ? (selectedCutsProp ?? specCutIds) : localSelectedCuts
  const setSelectedCuts = onSelectedCuts ?? setLocalSelectedCuts
  // Readability cap mirrors the diverging card: 3 cuts when comparing 2+ monadic items,
  // 5 on a single-question card (past the cap the picker disables further checkboxes).
  const maxCuts = maxCutsFor(r.monadic_rating?.length ?? 1)
  // Cut-id → picker label lookup (for the small-sample note + slice headers). Outside the
  // grouped picker menu an ambiguous bare value ("1", "2-4", "High") gets its group label
  // prefixed ("Locations: 1") so the note stays readable without the menu's headings.
  const cutLabelFor = useCallback(
    (cid: string): string | undefined => {
      for (const g of cutGroups) {
        const opt = g.options.find((o) => o.id === cid)
        if (opt) return qualifiedCutDisplayLabel(g.groupLabel, opt.label)
      }
      return undefined
    },
    [cutGroups],
  )
  // A settled result for THIS stage has landed responses for a built question. While the
  // stage is still querying we must NOT render the chart/tabs (they would show the prior
  // stage's series) — `hasData` folds the querying gate in so every chart/tab branch is
  // suppressed, and the chart region renders a loading mask instead (below).
  // WS-H (LP P3): the settled-but-stale case the Wave-1 `querying` mask doesn't cover — a
  // result whose responses belong to a previous stage (re-query / reload). Mask it like a
  // still-querying card so a prior stage's series never renders under this question.
  const stale = insightResponsesStale(r)
  const hasData = !querying && !stale && responses.length > 0 && Boolean(r.question?.type)
  // QS-26b — applying a "break down by X" chip selects those cuts silently, which is easy
  // to miss when the chart re-slices. Pulse the picker briefly so the marketer sees WHERE
  // the change landed. Self-clears via timeout.
  const [segmentPulse, setSegmentPulse] = useState(false)
  const flashSegment = useCallback(() => {
    setSegmentPulse(true)
  }, [])
  useEffect(() => {
    if (!segmentPulse) return
    const t = setTimeout(() => setSegmentPulse(false), 900)
    return () => clearTimeout(t)
  }, [segmentPulse])

  // QS-26 — unify the two segment mechanisms onto the client-side re-slice. A
  // "Break down by X" suggestion chip whose target attribute is available here is handled
  // by SELECTING that attribute's cuts in the picker (instant, no network) instead of
  // firing a new backend run that can fail ("Failed to fetch"). Non-segment chips still ask
  // the agent. The two controls can no longer contradict each other.
  const handleSuggestion = (s: { label: string; send: string; hint?: string; kind?: string }) => {
    if (segmentable) {
      const target = segmentTargetOf(s, segmentCandidates)
      if (target) {
        const ids = cutIdsForAttribute(cutGroups, target)
        if (ids.length) {
          setSelectedCuts(ids)
          flashSegment()
          return
        }
      }
    }
    // `fromSuggestion` suppresses the echoing user bubble — the choice shows in-card.
    // OVN-1 — forward the chip's lane (deepen|advance|angle) so the agent knows a probe
    // chip from an advance: deepen/angle turns run free-form instead of consuming the
    // next playbook stage (whose anchors + paired stem would compose an incoherent card).
    onAskNext?.(s.send, { fromSuggestion: true, suggestionKind: (s.kind || '').trim() || undefined })
  }
  // QS-8 — small-sample guard for the CHOSEN cuts: flag any selected cut whose base is
  // under MIN_SEGMENT_N so a thin slice isn't read with false confidence (the per-band
  // caveat inside the exhibit uses the tighter THIN_CELL_MIN; this is the card-level note).
  const segmentCellNotes = useMemo(
    () =>
      selectedCuts.length
        ? sliceResponsesByCutIds(responses, selectedCuts, cutLabelFor)
            .filter((slice) => slice.n > 0 && slice.n < MIN_SEGMENT_N)
            .map((slice) => ({ label: slice.label, n: slice.n }))
        : [],
    [responses, selectedCuts, cutLabelFor],
  )

  // QS-32 — render-only chart-type switch among the representations the backend marked
  // valid for this data shape (allowed_plots; already guarded, e.g. no 2-option pie).
  // QS-32b — controlled by the page when it owns the choice (so the report mirrors it);
  // otherwise local-state fallback keeps the card self-contained.
  const [localPlotOverride, setLocalPlotOverride] = useState<string>('')
  // Fernando ruling (2026-09-02): plot is a per-run LLM pick. Honor spec.plot when
  // it is in the shape allow-list (`allowed_plots`); do not force an arc-file plot.
  const specPlot =
    exhibitSpec?.plot &&
    Array.isArray(r.allowed_plots) &&
    r.allowed_plots.includes(exhibitSpec.plot)
      ? exhibitSpec.plot
      : undefined
  // NPS: prefer the existing NpsDivergingBar over a grouped_bar fall-through (M4).
  const npsDefaultPlot = resolveNpsAwarePlot(r.plot, r.metric || '', true)
  const plotOverride = onPlotOverride
    ? (plotOverrideProp ?? specPlot ?? npsDefaultPlot)
    : localPlotOverride || specPlot || npsDefaultPlot
  const selectPlot = onPlotOverride ?? setLocalPlotOverride
  // `plotOpts` (the pill options) is derived below, after `radarOffered` — a scorecard narrows
  // to Heatmap ⇄ Radar rather than its full backend-declared allowed_plots.

  // QS — render-only METRIC switch (mean ⇄ top-2-box %), mirroring the chart-type switch
  // above. Local-state only (no re-run, no backend call): it just re-derives the bar from
  // the same responses. Defaults to the result's own `metric`; only offered for the numeric
  // scorecard shapes ('mean' | 'top_2_box') — never '' (categorical/open) or 'nps'.
  const [localMetricOverride, setLocalMetricOverride] = useState<string>('')
  const specMetric =
    exhibitSpec?.metric && (exhibitSpec.metric === 'mean' || exhibitSpec.metric === 'top_2_box')
      ? exhibitSpec.metric
      : undefined
  const metricOverride = onMetricOverride
    ? (metricOverrideProp ?? specMetric ?? r.metric ?? '')
    : localMetricOverride || specMetric || r.metric || ''
  const selectMetric = onMetricOverride ?? setLocalMetricOverride
  // A single-question rating renders the intent SUMMARY (Top-2-box hero + mean stat + sentiment
  // bar) — both metrics live in that one view, so the mean⇄top-2-box switch is a no-op here and
  // its tabs are relabelled Summary/Distribution. The metric switch stays for monadic per-item
  // and matrix scorecards, where it actually reorders/recolours the bars.
  const isScalarRating = r.question?.type === 'rating' && !r.monadic
  // R8 — the diverging_stacked exhibit shows BOTH metrics as always-visible row stats
  // ("28% top-2 · avg 2.6") and its ONE card control is the Compare-segments picker, so
  // the mean⇄top-2-box Metric toggle AND the QS-32 View-as switcher are removed for this
  // shape ONLY (retired in the same change that ships the replacement exhibit). Cards
  // still rendering legacy plot ids ('rating', 'bar', …) keep their controls untouched —
  // the payload-driven plot id is the rollout flag. Alternate representations for
  // diverging_stacked cards live in the Report tab drill-in instead.
  const isDivergingStacked = exhibitPlot === 'diverging_stacked'
  // A monadic SCORECARD (dimensions × items) can also be read as a radar profile (dimensions =
  // axes, items = polygons), so this ONE diverging_stacked subset re-enables the View-as switch —
  // narrowed to Heatmap ⇄ Radar — while the plain diverging-rating and scalar-rating cards keep
  // theirs suppressed. Radar needs >=3 dimensions (a radial needs >=3 spokes); below that only the
  // heatmap renders and no pill shows. Both views read from the same `monadic_scorecard` aggregate
  // (PlotRenderer's `scorecardRadarData`), so no raw-response attributability gate is needed.
  const isScorecardCard = Boolean(r.monadic && (r.monadic_scorecard?.length ?? 0) > 0)
  // SC: a scorecard with Python's `scorecard_plot` offers Radar when that plot stamped a radar
  // alternative; a stored scorecard keeps the >=3 dimensions rule.
  const hasScorecardPlot = isScorecardCard && hasQuickStudyScorecardPlot(r)
  const radarOffered =
    isScorecardCard &&
    (hasScorecardPlot ? hasQuickStudyScorecardRadar(r) : (r.monadic_scorecard?.length ?? 0) >= 3)
  // R4.4 — toggles only subtract from structural capability predicates. Heuristics
  // stay the no-spec path. `!spec || spec.toggles.includes(id)`.
  const exhibitToggleAllowed = (id: string): boolean =>
    !exhibitSpec || (exhibitSpec.toggles ?? []).includes(id)
  // W6-QS: a stamped plot (no cut selected) owns its stat views. The switch shows only when that
  // plot offers Top N, and names its views as the plot does ("Top 3" on a 10-point scale); a plot
  // without Top N (a scale with no stored unit, 0-100) draws its own Average, so a click is never dead.
  // W7 T3b: a new read (`plot_inputs`) whose cut bands draw Python's stat plot keeps the payload's
  // views and words with cuts picked; any other cut view keeps the legacy labels.
  const stampedChoice =
    (!isScorecardCard || hasScorecardPlot) &&
    ((selectedCuts?.length ?? 0) === 0 || cutsDrawStampedStats(r, plotOverride || r.plot))
      ? stampedStatChoice(r)
      : null
  const capabilityMetricToggle =
    (r.metric === 'mean' || r.metric === 'top_2_box') &&
    !isScalarRating &&
    !isDivergingStacked &&
    (stampedChoice === null || stampedChoice.views.includes('topBoxes'))
  const metricLabelFor = (m: 'mean' | 'top_2_box'): string =>
    (m === 'mean' ? stampedChoice?.labels.mean : stampedChoice?.labels.topBoxes) ?? METRIC_LABELS[m]
  const showMetricToggle = capabilityMetricToggle && exhibitToggleAllowed('metric')
  const capabilityPlotSwitch = !isDivergingStacked || radarOffered
  const showPlotSwitch = capabilityPlotSwitch && exhibitToggleAllowed('plot_variant')
  // R4.5 — pre_post is spec-gated only (no heuristic). Absent spec stays
  // byte-identical; a spec that omits pre_post never invents the toggle.
  const showPrePostToggle =
    Boolean(exhibitSpec) && exhibitToggleAllowed('pre_post') && liftPair != null
  const prePostResult =
    showPrePostToggle && liftPair
      ? {
          ...r,
          question: (prePostHalf === 'pre' ? liftPair.pre : liftPair.post) as Result['question'],
        }
      : r
  // Joined by question id, never by the tag's measure token (which names the
  // whole read, not a row). One chip only when the pair has exactly one pooled
  // row: a per-asset or multi-measure lift has no single number to show here.
  const liftCard =
    showPrePostToggle && liftPair
      ? readLiftScorecardForPair(
          r,
          {
            post: liftQuestionIdsOf(liftPair.post as never),
            pre: liftQuestionIdsOf(liftPair.pre as never),
          },
          // A lift stored before grid rows were paired one by one is never shown.
          { postRowCount: compiledGridRowCount(liftPair.post) },
        )
      : null
  const liftOnlyRow = liftCard?.rows.length === 1 ? liftCard.rows[0] : null
  // A lift paired per grid row names its row (brand): a lone figure without
  // it reads as the whole grid's.
  const liftOnlyRowName = liftOnlyRow?.gridRowKey != null
    ? (liftOnlyRow.gridRowLabel ?? liftOnlyRow.gridRowKey)
    : null
  const liftDeltaRow =
    liftOnlyRow &&
    liftOnlyRow.pre != null &&
    liftOnlyRow.post != null &&
    liftOnlyRow.delta != null &&
    liftOnlyRow.direction != null
      ? {
          direction: liftOnlyRow.direction,
          label: [
            liftOnlyRowName,
            formatLiftTriplet(liftOnlyRow.pre, liftOnlyRow.post, liftOnlyRow.delta, liftOnlyRow.unit).delta,
          ]
            .filter(Boolean)
            .join(' '),
        }
      : null
  // Step 6 — every chat card now converges into `PlotCard`, including both open-end variants:
  // the OE card renders CONTROLLED (single active view) with its title/toggle/footnote lifted
  // into PlotCard slots, so no card keeps its own internal chrome anymore (`keepsOwnChrome` is
  // retired). `isMonadicOpenEnd` survives purely as a DETECTION: it routes the monadic card's
  // methodology footnote (`monadicCoverageNote`) and, in the report/preview (`!bubbled`) path,
  // still preserves the old "no generic coverage footer under the monadic exhibit" behavior.
  const isMonadicOpenEnd = Boolean(
    r.monadic &&
      (r.per_item?.length ?? 0) > 0 &&
      (isOpenEndPlotId(r.plot) ||
        r.question?.type === 'comment' ||
        r.question?.type === 'text'),
  )
  // Scorecard pill: the two meaningful reads only (the diverging_stacked mean heatmap + the radar
  // profile), dropping the Report-only 'heatmap' grid that also rides in allowed_plots. Every other
  // card keeps its full backend-declared allowed_plots — EXCEPT the raw `diverging_stacked` id.
  // Fix 2: `diverging_stacked` is a backend BASE plot id, never a distinct user-facing alternate.
  // It has no user-facing label (PLOT_LABELS omits it) and, for a matrix/scorecard shape, its
  // renderer draws the SAME grid the `heatmap`/`matrix` option already draws — so as a generic pill
  // it surfaced a raw "diverging_stacked" term that just duplicated the heatmap. Its only legitimate
  // pill appearance is the intentional radarOffered branch above (relabeled "Heatmap"), which builds
  // its own explicit list; the rating-distribution cards that legitimately render diverging_stacked
  // never expose a switch (showPlotSwitch is false for them). Filtering it here removes the raw,
  // duplicative choice without touching `r.allowed_plots` (so report/ledger persistence is intact)
  // or the diverging_stacked render branch (still the base exhibit for rating cards).
  // Monadic OE themes can be read as a sentiment heatmap (default, 2+ assets) or grouped bars.
  // Mirrors the scorecard's Heatmap⇄Radar switch: two synthetic plot ids ride plotOverride so the
  // choice persists via the ledger like every other chart-type switch. Gated to the themes tab at
  // the switch render sites below (clampedOpenEndTab isn't in scope yet here).
  const themeHeatmapOffered =
    isMonadicOpenEnd &&
    (r.monadic_themes?.length ?? 0) > 0 &&
    (r.per_item?.length ?? 0) >= 2
  const plotOptsRaw = radarOffered
    ? ['diverging_stacked', 'radar']
    : themeHeatmapOffered
      ? ['themes_heatmap', 'themes_bars']
      : (r.allowed_plots ?? []).filter((p) => p !== 'diverging_stacked')
  const plotOpts =
    (r.metric || '') === 'nps' && !plotOptsRaw.includes('nps_diverging')
      ? ['nps_diverging', ...plotOptsRaw]
      : plotOptsRaw

  // Wave 4 — the converged diverging/scorecard exhibit publishes its subtitle (scale anchors),
  // R10 footnote, and Compare-segments picker DATA up here (via `onDivergingChrome`), so the
  // outer PlotCard header/footer render the ONE title/subtitle/picker/footnote. Cut state lives
  // inside the exhibit; `picker.onChange` round-trips a header click back into it. Null for every
  // non-converged card (they use the generic inline chrome below).
  const [publishedChrome, setPublishedChrome] = useState<DivergingChromeData | null>(null)
  const handleDivergingChrome = useCallback((chrome: DivergingChromeData | null) => {
    setPublishedChrome(chrome)
  }, [])

  // Chart Compare-segments control visibility (unchanged). Segment *suggestion chips* are
  // retired separately below — the picker owns that UX, not the next-steps list.
  const segmentPickerShown =
    segmentable && cutGroups.some((g) => g.options.length > 0) && exhibitToggleAllowed('segments')
  // Hard-drop segment / "break down by …" chips so cached results / LLM drift cannot
  // resurface a move that used to hit the fabricated-segment honesty reply.
  const studySuggestions = useMemo(() => {
    const filtered = (r.suggestions ?? []).filter((s) => !isSegmentSuggestionChip(s))
    if (filtered.length > 0) return onAskNext || segmentable ? filtered : []
    const an = askNextSuggestion(r.ask_next)
    return an && onAskNext ? [{ label: an.label, send: an.send, hint: '', kind: '' }] : []
  }, [r.suggestions, r.ask_next, onAskNext, segmentable])

  // QS — consolidate affordances into ONE lettered a–c list with a short wrap-up intro
  // and exactly one Recommended move. The gates mirror the previous standalone buttons
  // (debate → split forced-choice; more-twins → too-close + headroom; focus group → handler).
  const showDebate =
    !!onDebate &&
    ['radiogroup', 'dropdown', 'ranking'].includes(String(r.question?.type)) &&
    isSplitResult({ agreement: r.agreement, spread_note: r.spread_note }) &&
    topTwoOptions({ question: r.question, responses }).length >= 2
  const showMoreTwins =
    !!onConfirmMore &&
    canTopUp({ tooClose: r.too_close_to_call, hasSpec: true, total: r.total, cap: confirmMoreCap ?? 150 })
  const nextSteps = useMemo(
    () => {
      if (!SHOW_QS_NEXT_STEPS) return []
      // The focus group seats up to CONVERSE_GROUP_CAP twins (the same slice launchConverse
      // uses), so the hint promises the count actually invited — never the full study N.
      const seated = Math.min(personas?.length ?? CONVERSE_GROUP_CAP, CONVERSE_GROUP_CAP)
      return buildNextSteps({
        suggestions: studySuggestions,
        showFocusGroup: !!onConverse,
        showDebate,
        showMoreTwins,
        answered,
        enough,
        advanceConsumed,
        tooClose: r.too_close_to_call === true,
        marginNote: r.margin_note,
        focusGroupHint: `Invite ${seated} twins to discuss ${focusGroupTopic(r)}`,
      })
    },
    [studySuggestions, onConverse, showDebate, showMoreTwins, r, personas],
  )
  // advanceAvailable: the arc still has a forward move (an `advance`-lane study chip). The
  // intro mirrors pickRecommendedKey — while a forward move exists, the count backstop must
  // not flip the copy to a wrap-up tone (respect the arc).
  const advanceAvailable = studySuggestions.some((s) => (s.kind || '').trim() === 'advance')
  const intro = nextStepsIntro(r, { answered, enough, advanceConsumed, advanceAvailable })

  // Prose-narrative migration (Step 2 FE) — the writer's flowing paragraphs supersede the
  // one-stat `bullets` shape below; gated FIRST so a session using the new writer never falls
  // through to the legacy structured shape. Precedence overall: prose → bullets → critique
  // (see the render branch below) — exactly one renders, never neither-with-content.
  const hasProseNarrative = Array.isArray(r.prose_paragraphs) && r.prose_paragraphs.length > 0
  // Group the resolvable testimonials by the paragraph they prove (`after_paragraph`). Each
  // quote is attributed STRICTLY off its server-stamped `persona_id` (resolveNarrativeQuoteAttribution)
  // — no fuzzy text-match fallback like the legacy `woven_quote` below. A quote with no id, or
  // one that doesn't match a fetched persona, has no honest attribution and never enters this
  // map — the render loop never has to special-case a bare, unattributed testimonial.
  const narrativeQuotesByParagraph = useMemo(() => {
    const map = new Map<number, Array<NarrativeQuote & NarrativeQuoteAttribution>>()
    for (const quote of r.quotes ?? []) {
      const attribution = resolveNarrativeQuoteAttribution(quote, personas ?? [], responses)
      if (!attribution) continue
      const idx = quote.after_paragraph ?? 0
      const bucket = map.get(idx) ?? []
      bucket.push({ ...quote, ...attribution })
      map.set(idx, bucket)
    }
    return map
  }, [r.quotes, personas, responses])
  // W7-T4b (Q16): with a stamped `quote_plot` the testimonials draw as the shared gallery's cards,
  // speaker and chips as Python joined them; a stored result keeps the persona lookup above.
  const stampedNarrativeQuotes = useMemo(() => {
    const plot = readQuotePlot(r.quote_plot)
    return plot ? { plot, byParagraph: narrativeQuotesFromPlot(plot, r.quotes ?? []) } : null
  }, [r.quote_plot, r.quotes])

  // Per-card storytelling optimization (Phase 2) — the structured narrative (bullets/
  // verdict/quote/segment_note) is the SECONDARY render path (prose supersedes it when
  // present, see `hasProseNarrative` above); `critique` markdown is the BACKWARD-COMPAT
  // fallback for a result generated/cached before the structured fields landed (a legacy
  // session, or a run started before the backend migration ships). Never more than one,
  // never neither-with-content — see the render branch below.
  const hasStructuredNarrative = Array.isArray(r.bullets) && r.bullets.length > 0
  const critiqueParts = useMemo(() => splitCritiqueLead(r.critique ?? ''), [r.critique])
  const comparisonLabels = useMemo(() => collectComparisonLabels(r), [r])
  const critiqueBody = useMemo(
    () => emphasizeComparisonLabels(critiqueParts.body, comparisonLabels),
    [critiqueParts.body, comparisonLabels],
  )
  // Subtle low-sample indicator (Step 2) — a quiet corner icon, never hedged prose. Gated
  // on the SAME base-size signal the footer already reads (isLowSampleCard reuses
  // TWINS_MIN, the codebase's existing "under this isn't statistically meaningful" floor).
  const lowSample = isLowSampleCard(r)

  // Dispatch a next-step click: record the in-card selection, then route to the move's
  // action. Study chips go through handleSuggestion (instant re-slice OR ask the agent);
  // the action items open a room / re-run / debate.
  const handleStepClick = (step: NextStepItem) => {
    onSelectStep?.(step.key)
    switch (step.kind) {
      case 'focus_group':
        onConverse?.()
        break
      case 'debate':
        onDebate?.()
        break
      case 'more_twins':
        onConfirmMore?.()
        break
      default:
        handleSuggestion({ label: step.label, send: step.send, hint: step.hint, kind: step.laneKind })
    }
  }

  // When bubbled, the template navigator + next steps split into a SECOND assistant bubble; it's
  // rendered only when there's something to show there (a forward move or a jumpable template).
  const hasNextContent = nextSteps.length > 0

  // QS plot-card standardization — the chat surface (`bubbled`) and the thread surface
  // (`chrome="plot-card"`) adopt the shared `PlotCard` shell for EVERY plot region (Step 6
  // folds the open-end cards in too): the segment picker, the View-as / Count|% toggles, the
  // Themes|Quotes view toggle, the title/eyebrow and the base-size footnote relocate into
  // PlotCard slots, and the chart (or the OE themes bars) renders as the ONE captured exhibit.
  // The report/preview surface (`!bubbled` + default `chrome`) keeps the existing inline layout
  // untouched. State stays here — only JSX moves into slots. OE segment widening, quotes
  // slicing, and AssistantActionCard bubbles stay gated on `bubbled` alone (not `chrome`).
  const effectivePlot = plotOverride || r.plot
  // Default resolves to the heatmap; 'themes_bars' flips to bars. Reads (plotOverride || r.plot)
  // because report surfaces bake the choice into the result's `plot` and pass no plotOverride.
  const resolvedThemeView = (plotOverride || r.plot) === 'themes_bars' ? 'themes_bars' : 'themes_heatmap'
  const isOpenEndPlot = isOpenEndPlotId(effectivePlot)
  // The OE card set for the Step-6 chat concerns (lifted toggle, themes-only export gate,
  // controlled tabs). A `comment`/`text` question routes to `renderOpenEndInsights` ONLY when its
  // plot falls through to the OE renderer — the `bar` case, the OE plot-id cases, or an
  // unrecognized plot id. Explicit-quant plots (pie/ranking/rating/radar/grouped_bar/quadrant/
  // matrix/nps_diverging/psm/…) render a chart even for a comment question, so they are NOT OE
  // cards. `routesToOpenEnd` (PlotRenderer, single source of truth) captures exactly that routing
  // on `effectivePlot`; pooled with the plot-id set and the monadic detection (T4, Fable review).
  const isOpenEndCard = isOpenEndPlot || isMonadicOpenEnd || routesToOpenEnd(effectivePlot, r.question)
  // The themes view is exportable only when it actually has bars: plain OE reads `r.themes`;
  // monadic reads the grouped-theme sources (`monadic_themes` / per-item themes) — mirrors the
  // monadic card's own `hasThemeContent`, so an empty-state themes view is never rasterizable.
  const hasExportableThemes = isMonadicOpenEnd
    ? (r.monadic_themes?.length ?? 0) > 0 || (r.per_item ?? []).some((it) => (it.themes?.length ?? 0) > 0)
    : // Mirror ThemesView's non-blank filter (`(t.theme || '').trim().length > 0`): an all-blank
      // themes array renders the empty state, so it must NOT seed `openEndTab='themes'` nor enable
      // export of a blank exhibit (T2, Fable review).
      (r.themes ?? []).some((t) => (t.theme || '').trim().length > 0)
  // PlotCard wraps chat cards (`bubbled`) and the thread twin-test surface (`chrome="plot-card"`);
  // the report/preview surface keeps the inline layout.
  const usePlotCard = bubbled || chrome === 'plot-card'
  // Chat-only Themes|Quotes state for the open-end card. Seeded from theme presence so a
  // themeless OE card opens on the Quotes view instead of the "No themes clustered" empty state
  // (review F5). `hasExportableThemes` reads the right source per variant (plain: `r.themes`;
  // monadic: `monadic_themes` / per-item themes), matching each card's own initializer.
  const [openEndTab, setOpenEndTab] = useState<OpenEndTab>(() => (hasExportableThemes ? 'themes' : 'quotes'))
  // T5 — the available OE views, computed ONCE (shared by the lifted toggle below and the clamp
  // here) from the SAME `availableOpenEndTabs` / `availableMonadicTabs` helpers the cards use, so
  // the toggle and the exhibit never disagree. Empty for non-OE cards / no data.
  const openEndTabs = useMemo<Array<OpenEndTab>>(() => {
    if (!(usePlotCard && isOpenEndCard && hasData)) return []
    return isMonadicOpenEnd
      ? availableMonadicTabs({
          documentMode: false,
          showQuotes: (r.per_item ?? []).some((it) => (it.responses?.length ?? 0) > 0),
        })
      : availableOpenEndTabs({ themes: r.themes, responses, documentMode: false, reportMode: false })
  }, [usePlotCard, isOpenEndCard, hasData, isMonadicOpenEnd, r.per_item, r.themes, responses])
  // The lifted toggle hides when only one view is available; if a re-query drops the responses
  // while `openEndTab==='quotes'`, the toggle vanishes and the controlled card would be stranded on
  // a view it can no longer switch off. Clamp the effective view to the available set so the
  // exhibit, the export gate and the quotes picker all fall back to the remaining view.
  const clampedOpenEndTab: OpenEndTab = openEndTabs.includes(openEndTab)
    ? openEndTab
    : openEndTabs[0] ?? 'themes'
  // ── Combo quant+OE tab model (Step G) ────────────────────────────────────────
  // A combo card renders ONE PlotCard with the tab strip [Chart · {combo_tab_label} · Quotes]:
  // Chart is the DEFAULT and is today's quant exhibit UNTOUCHED; the pairing tab shows the pooled
  // why-themes + a band-split control; Quotes shows the whys with a per-twin score badge. It is a
  // quant card (isOpenEndCard is false for it), so the OE chrome above stays inert — this is a
  // separate, presence-gated path.
  //
  // The whys ride on `qs_main_why`; the quant answer (the score-badge source) stays on the
  // question's own name key. The raw whys are the uncurated source of truth — NO per-twin gating
  // (the wire carries no per-twin outlier-guard flag; guard exclusions are server-side, applied
  // only to woven_quote + split clustering).
  const comboScoreKey = String(r.question?.name || COMBO_WHY_KEY.replace(/_why$/, ''))
  const comboWhyResponses = useMemo(
    () => (isComboCard ? responses.filter((resp) => comboWhyOf(resp).length > 0) : []),
    [isComboCard, responses],
  )
  const comboThemeCount = useMemo(
    () => (r.themes ?? []).filter((t) => (t.theme || '').trim().length > 0).length,
    [r.themes],
  )
  // The pairing (Why) tab is offered when there are pooled why-themes or a band split to show.
  const comboHasWhyView = comboThemeCount > 0 || (r.theme_splits?.bands?.length ?? 0) > 0
  const comboScoreBadge = useMemo(
    () => ({ scoreKey: comboScoreKey, scaleMax: comboScaleMaxFromQuestion(r.question) }),
    [comboScoreKey, r.question],
  )
  // Tab strip: Chart always; the pairing tab when there is a why view; Quotes when whys exist.
  const comboTabList = useMemo<Array<ComboTab>>(() => {
    if (!(usePlotCard && isComboCard && hasData)) return []
    return [
      'chart',
      ...(comboHasWhyView ? (['why'] as Array<ComboTab>) : []),
      ...(comboWhyResponses.length > 0 ? (['quotes'] as Array<ComboTab>) : []),
    ]
  }, [usePlotCard, isComboCard, hasData, comboHasWhyView, comboWhyResponses.length])
  const [comboTab, setComboTab] = useState<ComboTab>('chart')
  // Clamp to the available set (if a re-query drops the whys, fall back to Chart — never strand
  // the card on a tab it can no longer show). No auto-switching: default stays Chart.
  const clampedComboTab: ComboTab = comboTabList.includes(comboTab) ? comboTab : 'chart'
  // View-local by design: the combo card's `plotOverride` belongs to the Chart tab's quant plot,
  // and no report surface renders the Drivers band view, so there is nothing to persist.
  const [comboThemesView, setComboThemesView] = useState<'heatmap' | 'bars'>('heatmap')
  // On the Why/Quotes tabs the exhibit is the OE card, so all quant-chart chrome (segment picker,
  // plot switch, metric toggle, small-n caption, chart export) is suppressed for the combo card.
  const comboOffChart = isComboCard && clampedComboTab !== 'chart'
  // Step 7 — client-side segment cuts for the plain OE card's QUOTES view. THEMES stay unsliced
  // (`ThemeT` ships card-level aggregates with no per-response membership) and MONADIC OE is
  // excluded (its quotes come from `per_item[].responses`, out of this slicing channel's reach —
  // review F3). `segmentable` is FALSE for every OE plot, so the generic picker never fires here;
  // this dedicated gate re-slices ONLY the rendered quotes off the widened `cutGroups` (review F1).
  // Keyed off `isOpenEndCard` (not `isOpenEndPlot`) so a `bar`+comment card — which routes to the
  // OE renderer but carries a non-OE plot id — still gets the real quotes picker (T4).
  const quotesSegmentable =
    isOpenEndCard &&
    !isMonadicOpenEnd &&
    bubbled &&
    responses.length > 0 &&
    cutGroups.some((g) => g.options.length > 0)
  // The pre-sliced quote UNION: `sliceResponsesByCutIds` returns PER-CUT slices, so union their
  // matched responses, dedupe (a twin in two selected cuts appears once — review F7) and preserve
  // the base order by filtering the original list. Applied ONLY while the Quotes view is active
  // with cuts selected; the themes view, the export and the base-size footnote always reflect the
  // FULL base (quotes slicing is a view filter, not a re-base). `undefined` ⇒ the card gets the
  // full set.
  const openEndQuoteResponses = useMemo(() => {
    if (!quotesSegmentable || clampedOpenEndTab !== 'quotes' || selectedCuts.length === 0) return undefined
    const slices = sliceResponsesByCutIds(responses, selectedCuts, cutLabelFor)
    const matched = new Set(slices.flatMap((s) => s.responses))
    return responses.filter((resp) => matched.has(resp))
  }, [quotesSegmentable, clampedOpenEndTab, selectedCuts, responses, cutLabelFor])
  // T3 — a cut that matches NO twins slices `openEndQuoteResponses` to `[]` (not `undefined`). The
  // QuotesView must then say "No verbatims match the selected segments." instead of the false "None
  // captured" copy. Signalled down the SAME channel as the sliced responses (chat path only).
  const openEndQuoteFilteredEmpty = openEndQuoteResponses !== undefined && openEndQuoteResponses.length === 0
  // Whether the selected cuts actually slice the VISIBLE exhibit. For a quant card the exhibit is
  // always sliced by `selectedCuts`; for an OE card only the Quotes view is (the theme bars stay
  // full-base). So the cut-derived small-sample caption + the exhibit thin-cell suppression must
  // NOT fire under an unsliced OE themes view — otherwise a caveat describes a filter the visible
  // bars ignore (Fable review). `selectedCuts` is shared state, so gate on the OE view here.
  // For a combo card the cuts slice only the Chart tab's quant exhibit; the Why/Quotes tabs render
  // the OE card (full base), so the small-n caption must not describe a filter they ignore.
  const cutsSliceExhibit = isComboCard
    ? clampedComboTab === 'chart'
    : !isOpenEndCard || (clampedOpenEndTab === 'quotes' && quotesSegmentable)
  // Grammar (Fix 1): the narrative Sumana heading + stat eyebrow ALWAYS lead the card at the top
  // of `insightBody` (report/preview + every chat card alike) — they never relocate into PlotCard.
  // The PlotCard `title` slot is the question stem only (`display_title`, then construct_title,
  // then `r.title`). Versions and choice lists stay on the chart. Rating scale anchors go in
  // the muted subtitle. Suppressed only by the duplicate-title fallback (review F10): when there
  // is no narrative_title AND no headline the top heading itself falls back to `r.title`, so
  // feeding it again into the slot would print the same line twice.
  const chartTitle = usePlotCard && (r.narrative_title || r.headline) ? plotQuestionHeader(prePostResult) : undefined
  const chartScale = usePlotCard ? plotQuestionScale(prePostResult) : ''
  // Footnote source: converged diverging/scorecard cards use the exhibit-published footnote
  // (monadic-aware "N twins per creative, isolated reads"); the monadic open-end card carries its
  // methodology note (`monadicCoverageNote`, formerly rendered inline in PlotRenderer); every other
  // card — plain open-end included, and the NPS diverging view whose exhibit-internal n= line is
  // retired on chat (showBaseSize) — uses the generic coverage footnote. The `isMonadicOpenEnd`
  // DETECTION survives the removal of `keepsOwnChrome` (review F12a).
  // Combo (Step G) — the provenance footnote: one CONSTANT FE template over the wire stem, stating
  // both questions ("Scored first, then asked: …"). Composed with the base-size line via the card's
  // one-line " · " footer grammar so it renders on EVERY tab (Chart included) — the footnote slot is
  // tab-independent. The per-pairing footnote copy lives server-side (arc_registry.PAIRED_OE_FOOTNOTE);
  // this FE template promotes to a wire field if the two ever diverge.
  // An anchored-grid combo anchors each twin's OE on their OWN weakest dimension, so the per-twin
  // question differs — render the RULE-describing anchored footnote, never the single-`{stem}` echo
  // (which would misreport what was asked). Keyed off the server's `combo_anchored` wire flag, which
  // the backend stamps from the SAME D4 gate that decides anchoring (matrixdropdown + 2+ columns +
  // uniform, known polarity + higher-is-better). This REPLACES the old shape-only heuristic
  // (matrixdropdown && cols>=2), which misreported a balanced-emotion / mixed-polarity ODI grid as
  // anchored when its per-twin anchor is actually suppressed (a false methodology statement).
  const isAnchoredGridCombo = isComboCard && r.combo_anchored === true
  const comboProvenance = isAnchoredGridCombo
    ? COMBO_ANCHORED_GRID_FOOTNOTE
    : isComboCard && (r.combo_why_question || '').trim()
      ? `Scored first, then asked: "${(r.combo_why_question || '').trim()}"`
      : ''
  const plotCardFootnote = isComboCard
    ? // The provenance describes the OPEN-END question, not the chart — so on the Chart tab the
      // footer is just the base-size line; the "Scored first, then asked: …" note rides only on the
      // Why/Quotes tabs, where the verbatims that answer it actually live (`comboOffChart`).
      [coverageFooter(r.n, r.total, r), comboOffChart ? comboProvenance : ''].filter(Boolean).join(' · ')
    : isMonadicOpenEnd
      ? monadicCoverageNote(r)
      : isConvergedDiverging
        ? publishedChrome?.footnote
        : coverageFooter(r.n, r.total, r)
  // Export filename seeds from the narrative heading text (sanitized inside plotImageExport).
  const exportFilename = (r.narrative_title || r.headline || r.title || '').trim() || undefined

  // The card heading IS the key insight — the LLM narrative key-finding. Extracted so the SAME
  // content leads the card at top (report/preview + own-chrome chat cards) or fills the PlotCard
  // `title` slot (other chat cards). Embeds `ComparisonLabelText` + the grounding-withheld
  // tooltip button; the low-sample icon rides as an adjacent sibling.
  const narrativeTitleContent = (
    <>
      <ComparisonLabelText text={r.narrative_title || r.headline || r.title} labels={comparisonLabels} />
      {/* WS-1 grounding gate — the backend WITHHELD a winner/recommendation (a single-entity
          "leads" claim, an untraceable number, or a stimulus-dependent stage that ran without
          its stimulus). The grounded reason is already folded into r.headline; this icon
          makes the WITHHELD state discoverable without shouting — a quiet inline glyph
          right after the heading text, full reason on hover/focus via tooltip.
          Suppressed on prose cards: the flowing prose already states the withheld caveat
          ("within sampling error", "neither concept fully wins"), so the glyph is redundant. */}
      {!hasProseNarrative && (r.grounding?.verdict_withheld === true) && (r.grounding?.reason) && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Verdict withheld: ${r.grounding.reason}`}
                data-testid="qs-grounding-withheld"
                className="ml-1.5 inline-flex align-middle text-chart-warning/80 transition-colors hover:text-chart-warning focus-visible:text-chart-warning"
              >
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-xs">
              {r.grounding.reason}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  )
  // Step 2 — subtle low-sample indicator: a quiet corner icon + tooltip, NOT hedged prose.
  // Muted, small, gated on isLowSampleCard (reuses TWINS_MIN). Rides adjacent to the title.
  const lowSampleIcon = lowSample ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={t('quickStudyChrome.smallSampleSize')}
            data-testid="qs-low-sample-icon"
            className="mt-1 inline-flex shrink-0 text-muted-foreground/70 transition-colors hover:text-foreground"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] text-center text-xs">
          Small sample — a few twins can move these numbers, so lean on the bigger gaps.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null
  // The deterministic metric headline, demoted to a small stat eyebrow beneath the title. Only
  // when it ADDS information over the narrative title (never print the same line twice). W0.4:
  // strict !== was defeated by the server-side QS-53 caveat append (headline = title sentence +
  // caveat ⇒ always "different"), which duplicated the title as the dek — a normalized
  // restatement check suppresses that while a genuinely distinct metric line still renders.
  const statEyebrowContent =
    r.narrative_title && r.headline && !isHeadlineRestatement(r.narrative_title, r.headline) ? (
      <ComparisonLabelText text={r.headline} labels={comparisonLabels} />
    ) : null

  // ---- Plot-region chrome, extracted so both the inline (report/preview) layout and the chat
  // PlotCard slots compose from the SAME JSX (no duplication, no drift). ----
  // SEGMENT_CONTROL: squared toolbar icon + popover (option b) — chosen at the Wave-7
  // design gate: the inline "Compare segments" chip (option a) wrapped the header controls
  // onto a second/third row on crowded cards (View-as + Metric + picker + export toolbar),
  // squeezing the title column; the icon joins the export toolbar as one squared-icon
  // cluster. Discoverability tradeoff accepted and mitigated: hover `title`, aria-label,
  // a count badge when cuts are active, and the segmentPulse ring still applies. Flip back
  // by dropping `variant="icon"` (the labeled inline trigger remains in the component).
  // Nulled for any OE card (T4): an open-end card routes its exhibit through
  // `renderOpenEndInsights`, which ignores `selectedCuts`, so the generic quant picker would be a
  // dead control there. A `bar`+comment card makes `segmentable` (and thus `segmentPickerShown`)
  // true, so this guard is what suppresses that otherwise-dead picker; on the chat surface the OE
  // quotes view instead gets its segment picker through `filterPillsEl` (built below). This
  // `segmentControlEl` now serves ONLY the inline (report/preview, `!usePlotCard`) layout.
  const segmentControlEl = segmentPickerShown && !isOpenEndCard ? (
    <div
      className="flex items-center gap-2 text-sm"
      data-testid="qs-segment-control"
      data-pulsing={segmentPulse ? 'true' : undefined}
    >
      <CompareSegmentsPicker
        groups={cutGroups}
        selected={selectedCuts}
        onChange={setSelectedCuts}
        maxCuts={maxCuts}
        variant="icon"
        className={cn('transition-shadow', segmentPulse && 'ring-2 ring-twin-chat-send/40')}
      />
    </div>
  ) : null
  // Wave 4 / Step 7 — chat-surface cuts stay in the pill row (`filterPillsEl`); the corner
  // gear is view settings only (`settingsControlEl`). Both resolve the same DATA sources
  // (`publishedChrome` for the converged exhibit, `cutGroups`/`selectedCuts` for the
  // quotes/generic quant picker) — the two lanes never mix.
  // W7 T3b: the View-as pills offer Ranking and First choice (`bar`), so they pick a ranking's view.
  const rankingViewSwitch = hasData && showPlotSwitch && plotOpts.includes('ranking') && plotOpts.includes('bar')
  const plotSwitchEl =
    hasData && plotOpts.length > 1 && showPlotSwitch && (!themeHeatmapOffered || clampedOpenEndTab === 'themes') ? (
      <div className="flex flex-wrap items-center gap-1 text-[11px]" data-testid="qs-plot-switch">
        <span className="text-twin-chat-fg-muted">{t('quickStudyChrome.viewAs')}</span>
        {plotOpts.map((p) => {
          const active = (themeHeatmapOffered ? resolvedThemeView : (plotOverride || r.plot)) === p
          return (
            <button
              key={p}
              type="button"
              onClick={() => selectPlot(p)}
              className={cn(
                'rounded-full px-2 py-0.5 transition-colors',
                active
                  ? 'bg-twin-chat-send text-twin-chat-send-fg'
                  : 'border border-twin-chat-divider text-twin-chat-fg-muted hover:text-twin-chat-fg',
              )}
            >
              {themeHeatmapOffered && p === 'themes_heatmap'
                ? 'Heatmap'
                : themeHeatmapOffered && p === 'themes_bars'
                  ? 'Bars'
                  : isScalarRating && p === 'rating'
                    ? 'Summary'
                    : radarOffered && p === 'diverging_stacked'
                      ? 'Heatmap'
                      : (PLOT_LABELS[p] ?? p)}
            </button>
          )
        })}
      </div>
    ) : null
  // PlotCard surface — the "View as" chart-type switch lives in the corner view-settings
  // popover (never in the pill row). This carries the SAME options/labels/gate the retired
  // pill group used (`plotSwitchEl` above still serves the inline report/preview surface).
  // `selectPlot` round-trips the choice through the same page-owned override state, so
  // ledger/report persistence is unchanged. Undefined ⇒ no chart-type section (0–1 views).
  const chartTypeControl =
    hasData && plotOpts.length > 1 && showPlotSwitch && (!themeHeatmapOffered || clampedOpenEndTab === 'themes')
      ? {
          options: plotOpts.map((p) => ({
            id: p,
            label:
              themeHeatmapOffered && p === 'themes_heatmap'
                ? 'Heatmap'
                : themeHeatmapOffered && p === 'themes_bars'
                  ? 'Bars'
                  : isScalarRating && p === 'rating'
                    ? 'Summary'
                    : radarOffered && p === 'diverging_stacked'
                      ? 'Heatmap'
                      : (PLOT_LABELS[p] ?? p),
          })),
          selected: themeHeatmapOffered ? resolvedThemeView : (plotOverride || r.plot),
          onSelect: selectPlot,
        }
      : undefined
  // Pill-row cuts vs corner view-settings. Segment DATA still varies by card shape —
  // the converged diverging/scorecard exhibit publishes its picker (`publishedChrome`), the
  // plain-OE quotes view and the generic quant picker share `cutGroups`/`selectedCuts` —
  // but those cuts never enter the gear. The corner trigger stays reachable when there
  // are NO segments but 2+ chart types (e.g. the scorecard's radar view) so the switch
  // back is never stranded.
  const settingsSegmentSource: {
    groups: Array<import('../CompareSegmentsPicker').CompareSegmentsGroup>
    selected: Array<string>
    onChange: (ids: Array<string>) => void
    maxCuts: number
  } | null = isConvergedDiverging
    ? (publishedChrome?.picker ?? null)
    : clampedOpenEndTab === 'quotes' && quotesSegmentable
      ? { groups: cutGroups, selected: selectedCuts, onChange: setSelectedCuts, maxCuts }
      : segmentPickerShown && !isOpenEndCard
        ? { groups: cutGroups, selected: selectedCuts, onChange: setSelectedCuts, maxCuts }
        : null
  const viewSettingsSections = chartTypeControl
    ? [
        {
          id: 'plot-variant',
          label: t('quickStudyChrome.changeChartType'),
          options: chartTypeControl.options,
          selected: chartTypeControl.selected,
          onSelect: chartTypeControl.onSelect,
        },
      ]
    : []
  const settingsControlEl =
    viewSettingsSections.length > 0 ? (
      <div className="flex items-center gap-2 text-sm" data-testid="qs-view-settings">
        <ExhibitViewSettings
          sections={viewSettingsSections}
          triggerTestId={settingsSegmentSource ? 'qs-view-settings-trigger' : 'qs-compare-segments-trigger'}
          revealOnHover
        />
      </div>
    ) : null
  const filterPillsEl =
    settingsSegmentSource ? (
      <div
        className="flex items-center gap-2 text-sm"
        data-testid="qs-segment-control"
        data-pulsing={segmentPulse ? 'true' : undefined}
      >
        <CompareSegmentsPicker
          groups={settingsSegmentSource.groups}
          selected={settingsSegmentSource.selected}
          onChange={settingsSegmentSource.onChange}
          maxCuts={settingsSegmentSource.maxCuts}
          variant="inline"
          className={cn('transition-shadow', segmentPulse && 'ring-2 ring-twin-chat-send/40')}
        />
      </div>
    ) : null
  const metricToggleEl =
    hasData && showMetricToggle ? (
      <div className="flex flex-wrap items-center gap-1 text-[11px]" data-testid="qs-metric-switch">
        <span className="text-twin-chat-fg-muted">{t('quickStudyChrome.metric')}</span>
        {METRIC_OPTS.map((m) => {
          const active = metricOverride === m
          return (
            <button
              key={m}
              type="button"
              data-testid={`qs-metric-toggle-${m}`}
              onClick={() => selectMetric(m)}
              className={cn(
                'rounded-full px-2 py-0.5 transition-colors',
                active
                  ? 'bg-twin-chat-send text-twin-chat-send-fg'
                  : 'border border-twin-chat-divider text-twin-chat-fg-muted hover:text-twin-chat-fg',
              )}
            >
              {metricLabelFor(m)}
            </button>
          )
        })}
      </div>
    ) : null
  const prePostToggleEl =
    hasData && showPrePostToggle && liftPair ? (
      <div className="flex flex-wrap items-center gap-1 text-[11px]" data-testid="qs-pre-post-toggle">
        <span className="text-twin-chat-fg-muted">{t('quickStudyChrome.exposure')}</span>
        {(['pre', 'post'] as Array<'pre' | 'post'>).map((half) => {
          const isActive = prePostHalf === half
          return (
            <button
              key={half}
              type="button"
              data-testid={`qs-pre-post-toggle-${half}`}
              onClick={() => setPrePostHalf(half)}
              className={cn(
                'rounded-full px-2 py-0.5 transition-colors',
                isActive
                  ? 'bg-twin-chat-send text-twin-chat-send-fg'
                  : 'border border-twin-chat-divider text-twin-chat-fg-muted hover:text-twin-chat-fg',
              )}
            >
              {half === 'pre' ? 'Pre' : 'Post'}
            </button>
          )
        })}
        {liftDeltaRow ? (
          <span
            data-testid={`qs-lift-delta-${liftPair.measure}`}
            className={cn(
              'rounded-full px-2 py-0.5 tabular-nums',
              liftDeltaRow.direction === 'up' && 'bg-emerald-500/15 text-emerald-600',
              liftDeltaRow.direction === 'down' && 'bg-rose-500/15 text-rose-600',
              liftDeltaRow.direction !== 'up' &&
                liftDeltaRow.direction !== 'down' &&
                'bg-twin-chat-bubble text-twin-chat-fg-muted',
            )}
          >
            {liftDeltaRow.label}
          </span>
        ) : null}
      </div>
    ) : null
  // Step 6 — the lifted Themes|Quotes view toggle for the open-end card. It lives in the PlotCard
  // header controls cluster (outside the capture seam, so it can never rasterize into the export)
  // and drives the controlled `openEndTab` state threaded down to the card via PlotRenderer. Built
  // from the SAME `availableOpenEndTabs` / `availableMonadicTabs` helpers the cards use internally
  // so the two never drift; rendered only when there is more than one view. Styled as the same
  // segmented pill the card's own (now-suppressed) internal toggle wore.
  const openEndViewToggle =
    openEndTabs.length > 1 ? (
      <div
        className="flex items-center gap-0.5 rounded-full border border-twin-chat-divider bg-twin-chat-bg p-0.5"
        data-testid="qs-plot-view-toggle"
      >
        {openEndTabs.map((tabKind) => (
          <button
            key={tabKind}
            type="button"
            data-testid={`qs-plot-view-${tabKind}`}
            aria-pressed={clampedOpenEndTab === tabKind}
            onClick={() => setOpenEndTab(tabKind)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              clampedOpenEndTab === tabKind
                ? 'bg-twin-chat-surface text-twin-chat-fg'
                : 'text-twin-chat-fg-muted hover:text-twin-chat-fg',
            )}
          >
            {tabKind === 'themes' ? t('quickStudyChrome.themes') : t('quickStudyChrome.quotes')}
          </button>
        ))}
      </div>
    ) : null
  // Combo tab strip (Step G) — [Chart · {combo_tab_label} · Quotes] in the SAME PlotCard viewToggle
  // slot the OE Themes|Quotes toggle uses. Chart is the default; tab labels carry counts. The
  // pairing label is rendered VERBATIM from the wire (`combo_tab_label`) — never mapped kind→label
  // in the FE. No auto-switching.
  const comboViewToggle =
    comboTabList.length > 1 ? (
      <div
        className="flex items-center gap-0.5 rounded-full border border-twin-chat-divider bg-twin-chat-bg p-0.5"
        data-testid="qs-combo-tab-strip"
      >
        {comboTabList.map((tabKind) => {
          const label =
            tabKind === 'chart'
              ? t('quickStudyChrome.chart')
              : tabKind === 'why'
                ? `${(r.combo_tab_label || 'Why').trim()} · ${comboThemeCount}`
                : `${t('quickStudyChrome.quotes')} · ${comboWhyResponses.length}`
          return (
            <button
              key={tabKind}
              type="button"
              data-testid={`qs-combo-tab-${tabKind}`}
              aria-pressed={clampedComboTab === tabKind}
              onClick={() => setComboTab(tabKind)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                clampedComboTab === tabKind
                  ? 'bg-twin-chat-surface text-twin-chat-fg'
                  : 'text-twin-chat-fg-muted hover:text-twin-chat-fg',
              )}
            >
              {label}
            </button>
          )
        })}
      </div>
    ) : null
  // "Ask the follow-up" retrofit CTA (Step H, Part 4 / Step I wiring) — occupies the SAME
  // viewToggle slot the combo tab strip (above) and the OE Themes|Quotes toggle share. Eligible
  // only for a NON-combo card: `canRetrofit` already returns false once `paired_oe_kind` is set
  // (see quickStudyPure), so the moment retrofitFollowUp's merge lands, `isComboCard` flips true
  // and this ternary routes to `comboViewToggle` instead — the CTA and the tab strip are
  // therefore structurally mutually exclusive; there is no flicker / leftover CTA after the merge.
  // `hasData` mirrors the combo tab list's own gate (never show a card action over a still-
  // querying/stale masked chart). A blank/missing `pairedOeStem` means the agent never stamped a
  // pairing for this stage — nothing renders, same as a stage with no registry pairing at all.
  const retrofitStem = (pairedOeStem ?? '').trim()
  const retrofitEligible = usePlotCard && !isComboCard && hasData && retrofitStem.length > 0 && canRetrofit(r)
  // TODO(fast-follow, per the combo plan note): the CTA label is generic ("Ask the follow-up") for
  // every pairing kind; a per-pairing label (e.g. "Ask why" vs. "Ask what would win them") needs a
  // wire hint — the label lives server-side in arc_registry only, not yet exposed on
  // GenerationSpec. Until then every pairing gets the same label; the stamped stem itself (shown
  // as the native `title` tooltip, matching this card's other plain-tooltip affordances) is what
  // makes the CTA pairing-aware to the marketer.
  const retrofitCta =
    retrofitEligible && onRetrofit ? (
      <button
        type="button"
        data-testid="quant-card-retrofit-cta"
        title={retrofitStem}
        disabled={retrofitting}
        onClick={onRetrofit}
        className="flex items-center gap-1.5 rounded-full border border-twin-chat-divider bg-twin-chat-bg px-3 py-1 text-xs font-medium text-twin-chat-fg-muted transition-colors hover:text-twin-chat-fg disabled:pointer-events-none disabled:opacity-50"
      >
        {retrofitting && <TwinsMarkLoader className="h-3 w-3" />}
        {retrofitting ? t('quickStudyChrome.addingFollowUp') : t('quickStudyChrome.askFollowUp')}
      </button>
    ) : null
  // The content/view toggle (combo Chart·Drivers·Quotes, the OE Themes|Quotes switch, or the
  // retrofit CTA) is a control ABOUT the card, not part of the plot — so it renders ABOVE the
  // card, right-aligned, instead of inside the PlotCard header (see the usePlotCard branch).
  const cardViewToggle = isComboCard ? comboViewToggle : (openEndViewToggle ?? retrofitCta)
  // MONADIC-combo Drivers view: when the server coded the whys across versions into a shared
  // codeframe (`combo_version_themes`, populated ONLY for a monadic combo — each twin saw one
  // version), the Drivers/Why tab renders the SAME grouped bar the standalone monadic OE card
  // uses (consolidated themes, one bar per version), NOT the per-band `theme_splits` chips. Every
  // other case — a non-monadic combo (empty `combo_version_themes`) OR the Quotes tab — keeps the
  // existing `QuickStudyOpenEndCard` path, so the `theme_splits` chip fallback is preserved.
  const comboVersionThemes = r.combo_version_themes ?? []
  const showGroupedComboDrivers = comboVersionThemes.length > 0 && clampedComboTab === 'why'
  // Path B's heatmap eligibility, memoized once per result (the same derivation ThemesView
  // repeats internally — cheap, but this file memoizes aggressively so match the idiom).
  const comboBandHeat = useMemo(() => bandThemesToHeatmap(r.theme_splits ?? null), [r.theme_splits])
  // Path A needs 2+ COLUMNS to heatmap (GroupedThemeBar falls back to bars below that), so gate
  // the toggle on the same column derivation — a one-column monadic combo (only one version
  // produced codeable whys) must not offer a switch that visibly does nothing.
  const comboVersionColumns = useMemo(
    () => deriveThemeColumns([], comboVersionThemes),
    [comboVersionThemes],
  )
  // Heatmap⇄Bars applies on the Drivers tab when EITHER drivers path can actually heatmap: the
  // shared version codeframe with 2+ columns (Path A) or a shared band codeframe (Path B).
  const comboThemesToggleOffered =
    isComboCard &&
    clampedComboTab === 'why' &&
    ((showGroupedComboDrivers && comboVersionColumns.length >= 2) || comboBandHeat !== null)
  const comboDriverVariantColor = useMemo(() => {
    const stubs = comboVersionThemes
      .flatMap((t) => t.variants ?? [])
      .map((v) => ({ creative_id: v.creative_id, label: v.label, n: 0, responses: [] }))
    return buildVariantColors(stubs, getAppChartPalette())
  }, [comboVersionThemes])
  // Combo Why/Quotes exhibit — the paired open-end rendered via the SAME controlled-tab OE card
  // embedding (activeTab), extended with the band-split control + score badges. The whys are the
  // raw source of truth (no per-twin gating). Chart tab keeps today's quant exhibit untouched.
  const comboOeRegion = showGroupedComboDrivers ? (
    <div className="pt-1" data-testid="qs-combo-version-drivers">
      <GroupedThemeBar
        themes={comboVersionThemes}
        perItem={[]}
        variantColor={comboDriverVariantColor}
        comparisonLabels={comparisonLabels}
        themesView={comboThemesView}
        heatPlot={readThemeHeatPlot(r.combo_version_heat_plot)}
      />
    </div>
  ) : (
    <QuickStudyOpenEndCard
      themes={r.themes}
      responses={comboWhyResponses}
      question={r.question}
      personas={personas}
      populationId={populationId}
      comparisonLabels={comparisonLabels}
      activeTab={clampedComboTab === 'why' ? 'themes' : 'quotes'}
      themeSplits={r.theme_splits}
      whyKey={COMBO_WHY_KEY}
      scoreBadge={comboScoreBadge}
      versionLabelById={versionLabelById}
      themesView={comboThemesView}
      themeSplitsPlot={r.theme_splits_plot}
      quotePlot={r.quote_plot}
    />
  )
  // Thread / chat plot-card: a monadic OE that is NOT a combo and has no `per_item` list
  // (the twin-test projection often carries labels on `monadic_*` / `question.rows` only)
  // otherwise lands in PlotRenderer's plain QuickStudyOpenEndCard with no version map.
  // Host that quotes view here so the shared chip machinery still runs.
  const showMonadicPlainOeQuotes =
    usePlotCard &&
    !isComboCard &&
    Boolean(r.monadic) &&
    isOpenEndCard &&
    !isMonadicOpenEnd &&
    clampedOpenEndTab === 'quotes'
  const monadicPlainOeQuotesRegion = (
    <QuickStudyOpenEndCard
      themes={r.themes}
      responses={openEndQuoteResponses ?? responses}
      question={r.question}
      personas={personas}
      populationId={populationId}
      comparisonLabels={comparisonLabels}
      activeTab="quotes"
      filteredEmpty={openEndQuoteFilteredEmpty}
      versionLabelById={versionLabelById}
      quotePlot={r.quote_plot}
    />
  )
  // Drivers-tab Heatmap⇄Bars switch — the same view-settings popover the chart-type
  // switch uses, standing in for `settingsControlEl` on this tab (the combo card
  // suppresses the quant settings control off the Chart tab — see the PlotCard
  // `segmentControl` slot below).
  const comboThemesControlEl = comboThemesToggleOffered ? (
    <div className="flex items-center gap-2 text-sm" data-testid="qs-combo-themes-view-control">
      <ExhibitViewSettings
        sections={[
          {
            id: 'plot-variant',
            label: t('quickStudyChrome.changeChartType'),
            options: [
              { id: 'heatmap', label: 'Heatmap' },
              { id: 'bars', label: 'Bars' },
            ],
            selected: comboThemesView,
            onSelect: (id) => setComboThemesView(id === 'bars' ? 'bars' : 'heatmap'),
          },
        ]}
        triggerTestId="qs-compare-segments-trigger"
        revealOnHover
      />
    </div>
  ) : null
  const chartRegionEl =
    querying || stale ? (
      // WS-1: this stage's own result hasn't settled — mask the chart region instead of
      // leaking the prior stage's series under a still-querying question.
      // WS-H (LP P3): same mask for a SETTLED-but-stale panel (responses from a previous
      // stage on re-query/reload) — never render a prior stage's series under this question.
      <div
        className="flex items-center gap-2 rounded-lg border border-twin-chat-divider bg-twin-chat-bubble/20 px-3 py-6 text-sm text-twin-chat-fg-muted"
        data-testid="qs-insight-loading-mask"
      >
        <TwinsMarkLoader />
        <span>{querying ? t('quickStudyChrome.queryingTwins') : t('quickStudyChrome.updatingQuestion')}</span>
      </div>
    ) : hasData ? (
      // Combo Why/Quotes tab → the paired-OE exhibit; every other case (incl. the combo Chart tab)
      // → today's quant/OE PlotRenderer, byte-identical.
      comboOffChart ? (
        comboOeRegion
      ) : showMonadicPlainOeQuotes ? (
        monadicPlainOeQuotesRegion
      ) : (
      /* Wave-7 caveat dedup: when the card-level named note (`qs-small-n`) is showing, the
         exhibit's blanket thin-cell line is suppressed so ONE honest line rides per card. */
      <BandCaveatSuppressedContext.Provider value={segmentCellNotes.length > 0 && cutsSliceExhibit}>
        <PlotRenderer result={prePostResult} plotOverride={plotOverride} metricOverride={metricOverride} metricSwitch={Boolean(metricToggleEl)} rankingViewSwitch={rankingViewSwitch} selectedCuts={segmentable ? selectedCuts : undefined} cutGroups={segmentable ? cutGroups : undefined} personas={personas} populationId={populationId} intentView audienceType={audienceType} onDivergingChrome={usePlotCard && isConvergedDiverging ? handleDivergingChrome : undefined} openEndTab={usePlotCard && isOpenEndCard ? clampedOpenEndTab : undefined} onOpenEndTabChange={usePlotCard && isOpenEndCard ? setOpenEndTab : undefined} openEndQuoteResponses={openEndQuoteResponses} openEndQuoteFilteredEmpty={openEndQuoteFilteredEmpty} />
      </BandCaveatSuppressedContext.Provider>
      )
    ) : (
      <p className="text-sm text-twin-chat-fg-muted">{t('quickStudyChrome.noChartForRun')}</p>
    )
  const smallNNoteEl =
    segmentCellNotes.length > 0 && cutsSliceExhibit ? (
      <p className="text-[11px] text-amber-500" data-testid="qs-small-n">
        ⚠ Small sample: {segmentCellNotes.map((c) => `${c.label} (n=${c.n})`).join(', ')} — a couple of twins can swing these splits.
      </p>
    ) : null
  const emptyCutNoteEl =
    exhibitSpec && emptyBaseCuts.length > 0 ? (
      <div className="space-y-1" data-testid="qs-exhibit-empty-cut">
        <div className="flex flex-wrap gap-1">
          {emptyBaseCuts.map((c) => {
            const emptyCutLabel = cutLabelFor(c.cutId) ?? cutIdDisplayLabel(c.cutId)
            return (
              <span
                key={c.cutId}
                className="rounded-full border border-twin-chat-divider px-2 py-0.5 text-[11px] text-twin-chat-fg-muted opacity-60"
                data-testid="qs-exhibit-empty-cut-chip"
              >
                {emptyCutLabel} (n=0)
              </span>
            )
          })}
        </div>
        <p className="text-[11px] text-amber-500" data-testid="qs-exhibit-empty-cut-note">
          No twins in {emptyBaseCuts.map((c) => cutLabelFor(c.cutId) ?? cutIdDisplayLabel(c.cutId)).join(', ')} — this segment is empty on this run.
        </p>
      </div>
    ) : null
  const exhibitTakeawayEl =
    exhibitSpec && (r.takeaway || '').trim() ? (
      <TakeawayBlock text={(r.takeaway || '').trim()} dataTestId="qs-insight-takeaway" />
    ) : null
  // Honesty note first, authored takeaway below. No-spec → `smallNNoteEl` only (identical).
  const plotCaptionEl =
    exhibitSpec && (smallNNoteEl || emptyCutNoteEl || exhibitTakeawayEl) ? (
      <div className="space-y-1.5" data-testid="qs-exhibit-caption">
        {smallNNoteEl}
        {emptyCutNoteEl}
        {exhibitTakeawayEl}
      </div>
    ) : (
      smallNNoteEl
    )

  const showNarrativeRegion = region !== 'plot'
  const showPlotRegion = region !== 'narrative'

  const insightBody = (
    <>
      {showNarrativeRegion ? (
      <>
      <div className="space-y-3">
        {/* The card heading IS the key insight — the LLM narrative key-finding (we are
            storytellers using data to support). It ALWAYS leads the card here — report/preview
            and every chat card alike (Fix 1); the PlotCard `title` slot below carries the chart
            construct title (`r.title`) instead, never this narrative heading. The deterministic
            metric (r.headline) is demoted to a small stat eyebrow. Graceful fallback: with no
            narrative_title the metric headline becomes the heading and the eyebrow is omitted
            (never the same line twice). The low-sample icon rides as a sibling. */}
        {/* Prose-narrative cards: the title is a plain BOLD line at body font-size (not the big
            serif heading), and the grey stat/summary eyebrow below is dropped, so the card reads as
            a simple bold title + flowing paragraphs (no heading / subheading / paragraph triple that
            all opened with the same words). Legacy (bullets / critique) cards keep the serif heading
            and the demoted stat eyebrow unchanged. */}
        <div className="flex items-start justify-between gap-2">
          {hasProseNarrative ? (
            <Text
              as="p"
              size="base"
              className="font-semibold leading-relaxed text-twin-chat-fg"
              data-testid="qs-narrative-title"
            >
              {narrativeTitleContent}
            </Text>
          ) : (
            <h3 className="font-display text-2xl font-normal leading-normal tracking-tight text-twin-chat-fg">
              {narrativeTitleContent}
            </h3>
          )}
          {lowSampleIcon}
        </div>
        {statEyebrowContent && !hasProseNarrative && (
          <p className="text-sm tabular-nums text-twin-chat-fg-muted" data-testid="qs-stat-line">
            {statEyebrowContent}
          </p>
        )}
        {r.low_variance && (
          <p className="rounded-md bg-amber-500/10 px-2.5 py-1.5 text-sm text-amber-500">
            {/* QS-91 — branch the hint: only blame "creatives / can they see them" when a
                stimulus image was actually shown for this result; a text-only run gets
                copy that fits an all-text comparison. */}
            ⚠ Near-unanimous — the twins barely differentiated. {lowVarianceHint(r.question)}
          </p>
        )}
        {/* ★ Stimulus grounding — this run had a creative in the study but the twins
            answered with NO stimulus image (the silent-ungrounded case). Surface it so a
            text-only answer about a creative isn't mistaken for a grounded one. */}
        {ranTextOnly(r.question, studyHadCreative === true) && (
          <p
            className="mt-1 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-500"
            data-testid="qs-text-only-badge"
          >
            ⚠ Ran text-only — the twins answered without seeing your creative for this question. Re-attach it to ground the answer.
          </p>
        )}
        {/* The narrative read — the centerpiece (who won → why → what to do). No label:
            the title above is the heading; this flows beneath it like a marketer brief.
            Precedence (Step 2, prose-narrative migration): PROSE first — the writer's
            flowing paragraphs, with testimonials interleaved after the paragraph they prove
            and one opinionated beat per surviving subsegment cut — else the STRUCTURED
            shape (thesis heading above → bullets → verdict → optional woven quote →
            optional segment note) for a result predating the prose migration — else the
            backward-compat markdown `critique` blob for a result with none of the
            structured fields (a legacy session, or a run started before the backend
            migration ships). Exactly one renders; the card must never go blank just
            because newer fields are absent. */}
        {hasProseNarrative ? (
          <div className="space-y-3" data-testid="qs-narrative-prose">
            {r.prose_paragraphs!.map((paragraph, i) => (
              <Fragment key={i}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => (
                      <Text
                        as="p"
                        size="base"
                        className="leading-relaxed text-twin-chat-fg"
                        data-testid="qs-narrative-paragraph"
                      >
                        {children}
                      </Text>
                    ),
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  }}
                >
                  {paragraph}
                </ReactMarkdown>
                {/* Testimonials interleaved right after the paragraph they prove
                    (`after_paragraph`). Attribution is resolved STRICTLY off the
                    server-stamped `persona_id` (resolveNarrativeQuoteAttribution) — an
                    unresolvable quote never reaches this map, so it never renders bare.
                    Step 4b: the left-border quote block is now the shared framed
                    AttributedQuoteCard (`compact`, so two quotes plus a chart don't
                    overwhelm the card), same testids, now carried via `dataTestId` /
                    `attributionTestId`, with the item/segment chips passed through `chips`. */}
                {stampedNarrativeQuotes ? (stampedNarrativeQuotes.byParagraph.get(i) ?? []).map((quote) => (
                  <NarrativeQuotePlotCard key={quote.id} plot={stampedNarrativeQuotes.plot} quote={quote} personas={personas} comparisonLabels={comparisonLabels} />
                )) : (narrativeQuotesByParagraph.get(i) ?? []).map((quote, qi) => (
                  <AttributedQuoteCard
                    key={`${i}-${qi}`}
                    compact
                    quote={quote.text}
                    name={quote.name}
                    blurb={quote.blurb}
                    initials={quote.initials}
                    imageUrl={quote.imageUrl}
                    dataTestId="qs-narrative-quote"
                    attributionTestId="qs-narrative-quote-attribution"
                    chips={
                      <>
                        {/* Monadic version chip: which creative/page this twin reviewed. */}
                        {quote.item_label && (
                          <span
                            className="shrink-0 rounded-full bg-twin-chat-bubble/60 px-2 py-0.5 text-xs font-medium text-twin-chat-fg"
                            data-testid="qs-narrative-quote-item-chip"
                          >
                            {quote.item_label}
                          </span>
                        )}
                        {quote.segmentLabel && (
                          <SubsegmentChip
                            label={quote.segmentLabel}
                            className="align-middle"
                            testId="qs-narrative-quote-segment-chip"
                          />
                        )}
                      </>
                    }
                  />
                ))}
              </Fragment>
            ))}
            {/* One opinionated beat per surviving subsegment cut — bold title, no separate
                heading element (unlike the legacy SegmentNoteSection below). */}
            {(r.segment_paragraphs ?? []).map((segment, j) => (
              <Text
                key={j}
                as="p"
                size="base"
                className="leading-relaxed text-twin-chat-fg"
                data-testid="qs-narrative-segment-paragraph"
              >
                <strong className="font-semibold">{segment.title}.</strong> {segment.text}
              </Text>
            ))}
            {/* Step 4b: the one-line marketer so-what, via the shared TakeawayBlock. Only
                when the writer actually sent one: absent/empty `takeaway` keeps this render
                byte-identical to before. */}
            {(r.takeaway || '').trim() && !exhibitSpec ? (
              <TakeawayBlock text={r.takeaway!.trim()} dataTestId="qs-insight-takeaway" />
            ) : null}
            {/* The deterministic thin-read/sub-floor/all-empty honesty caveat still surfaces
                as the same quiet tooltip icon — just without the legacy segment-note BODY
                (superseded by `segment_paragraphs` above). No `segmentNote` passed → the
                section renders only when there's a caveat to show. */}
            <SegmentNoteSection
              segmentNoteTitle={r.segment_note_title}
              segmentNoteCaveat={r.segment_note_caveat}
              comparisonLabels={comparisonLabels}
            />
          </div>
        ) : hasStructuredNarrative ? (
          <div className="space-y-3" data-testid="qs-narrative">
            <ul className="space-y-1.5" data-testid="qs-narrative-bullets">
              {r.bullets!.map((bullet, i) => (
                <li key={i} className="flex gap-2" data-testid="qs-narrative-bullet">
                  <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-twin-chat-fg-muted" />
                  <Text as="span" size="base" className="leading-relaxed text-twin-chat-fg">
                    <ComparisonLabelText text={bullet.claim} labels={comparisonLabels} />
                    {bullet.stat && (
                      <span className="ml-1 font-medium tabular-nums text-twin-chat-fg-muted">
                        {bullet.stat}
                      </span>
                    )}
                  </Text>
                </li>
              ))}
              {/* The representative verbatim, folded in as the LAST bullet so it reads as part of
                  the evidence list (same bullet style) rather than a detached blockquote — and it
                  always sits just before the verdict closure below. The attribution row names the
                  twin (see wovenQuoteAttribution), never invented. */}
              {r.woven_quote && (
                <li className="flex gap-2" data-testid="qs-narrative-quote">
                  <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-twin-chat-fg-muted" />
                  <div className="min-w-0">
                    {/* Quote + attribution flow inline in regular text with the segment chip
                        trailing. Present only when the twin was identified. */}
                    <Text as="span" size="base" className="leading-relaxed text-twin-chat-fg">
                      <span>“{r.woven_quote}”</span>
                      {wovenQuoteAttribution && (
                        <span data-testid="qs-narrative-quote-attribution">
                          {' '}
                          {wovenQuoteAttribution.name}
                          {wovenQuoteAttribution.blurb && (
                            <span className="text-twin-chat-fg-muted"> ({wovenQuoteAttribution.blurb})</span>
                          )}
                          {wovenQuoteAttribution.segmentLabel && (
                            <SubsegmentChip
                              label={wovenQuoteAttribution.segmentLabel}
                              className="ml-1.5 align-middle"
                              testId="qs-narrative-quote-segment-chip"
                            />
                          )}
                        </span>
                      )}
                    </Text>
                  </div>
                </li>
              )}
            </ul>
            {r.verdict && (
              <Text
                as="p"
                size="base"
                weight="medium"
                className="leading-relaxed text-twin-chat-fg"
                data-testid="qs-narrative-verdict"
              >
                <ComparisonLabelText text={r.verdict} labels={comparisonLabels} />
              </Text>
            )}
            <SegmentNoteSection
              segmentNote={r.segment_note}
              segmentNoteTitle={r.segment_note_title}
              segmentNoteCaveat={r.segment_note_caveat}
              comparisonLabels={comparisonLabels}
            />
          </div>
        ) : r.critique ? (
          <div
            className="space-y-3 text-base leading-relaxed text-twin-chat-fg [&_strong]:font-semibold"
            data-testid="qs-critique"
          >
            {critiqueParts.lead && (
              <p className="leading-relaxed" data-testid="qs-critique-lead">
                <ComparisonLabelText text={critiqueParts.lead} labels={comparisonLabels} />
              </p>
            )}
            {critiqueParts.body && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <p className="leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="ml-4 list-disc space-y-1.5">{children}</ul>,
                  ol: ({ children }) => <ol className="ml-4 list-decimal space-y-1.5">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                }}
              >
                {critiqueBody}
              </ReactMarkdown>
            )}
          </div>
        ) : null}
        {/* Fable review 🟠1 — a monadic read that FAILED still carries its honesty caveat on
            `segment_note`/`segment_note_caveat` with empty bullets AND empty critique (the
            dropped-cut note the note-loss fix exists to surface). The structured branch
            renders the segment note section inline, and the critique-fallback branch
            already embeds a legacy-folded note via _compose_critique; this standalone
            render is ONLY for the both-empty (non-prose) case. The prose branch renders its
            OWN caveat-only SegmentNoteSection inline above, so it's excluded here too — guard
            on !hasProseNarrative AND !hasStructuredNarrative AND !r.critique so it never
            double-renders (I2 🟡2). */}
        {!hasProseNarrative && !hasStructuredNarrative && !r.critique && (
          <SegmentNoteSection
            segmentNote={r.segment_note}
            segmentNoteTitle={r.segment_note_title}
            segmentNoteCaveat={r.segment_note_caveat}
            comparisonLabels={comparisonLabels}
          />
        )}
        {r.dissent && r.dissent.count > 0 && (
          <p className="text-sm leading-relaxed text-twin-chat-fg-muted" data-testid="qs-dissent">
            ↔ <span className="font-medium text-twin-chat-fg">{r.dissent.count}</span> of {r.n} leaned{' '}
            <span className="font-medium text-twin-chat-fg">
              <ComparisonLabelText text={r.dissent.label} labels={comparisonLabels} />
            </span>{' '}
            ({r.dissent.percent}%)
            {r.dissent.attribute_skew ? ` — ${r.dissent.attribute_skew}` : ''}
            {r.dissent.why ? (
              <>
                : “<ComparisonLabelText text={r.dissent.why} labels={comparisonLabels} />”
              </>
            ) : ''}
          </p>
        )}
      </div>

      {/* QS-34 — auto key-segment podium, only present when the marketer asked
          "who is it best for" (the backend gates on segment_discovery + an n>=8 floor).
          Each cell shows its size + affinity, and the hard directional caveat rides below. */}
      {(r.key_segments?.length ?? 0) > 0 && (
        <div
          className="space-y-1.5 rounded-lg border border-twin-chat-divider bg-twin-chat-bubble/20 p-3"
          data-testid="qs-key-segments"
        >
          <p className="text-xs font-medium text-twin-chat-fg-muted">{t('quickStudyChrome.whoRespondsBest')}</p>
          <ol className="space-y-1">
            {r.key_segments!.slice(0, 3).map((s, i) => (
              <li key={`${s.attribute}-${s.value}`} className="flex items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 truncate text-twin-chat-fg">
                  <span className="mr-1 text-twin-chat-fg-muted">{i + 1}.</span>
                  {s.label}
                </span>
                <span className="shrink-0 text-xs text-twin-chat-fg-muted">
                  {Math.round(s.affinity * 100)}% · n={s.n}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
      </>
      ) : null}

      {/* Subsegment cuts are no longer a standalone card — the subsegment analysis is now WOVEN
          into the main insight (title / lead / bullets + an optional subsegment-only closing
          paragraph), produced by the critique/monadic-read LLM from the backend `segment_cuts`
          (which are still computed, just not rendered separately). */}

      {/* The debate / focus-group / run-more-twins moves are no longer standalone
          buttons here — they're folded into the consolidated "Suggested next steps"
          list below the chart (see buildNextSteps), each under the same gate. */}

      {showPlotRegion ? (usePlotCard ? (
       <>
        {/* The content/view toggle rides ABOVE the card, right-aligned — it steers what the card
            shows and doesn't belong inside the plot chrome. Adds a little air below the analysis. */}
        {cardViewToggle && (
          <div className="flex justify-end" data-testid="qs-card-view-toggle">
            {cardViewToggle}
          </div>
        )}
        {
        // Chat card — the standardized shell (Step 6 folds the open-end cards in too). The segment
        // picker, the View-as / Count|% toggles, the Themes|Quotes view toggle and the base-size /
        // methodology footnote relocate into slots; the chart (or the OE themes bars) is the ONE
        // captured exhibit. The PlotCard `title` slot carries the question stem (`chartTitle`
        // = display_title + scale labels, suppressed only by the F10 duplicate-title fallback); the narrative heading +
        // stat eyebrow stay at the top of the card (Fix 1). Converged diverging/scorecard cards feed
        // the subtitle/footnote/picker from the exhibit's published channel (`publishedChrome`); the
        // open-end card renders CONTROLLED (single active view) via `openEndTab` and gates its export
        // to the themes view (`enableImageExport`).
        <PlotCard
          title={chartTitle}
          subtitle={isConvergedDiverging ? publishedChrome?.subtitle : (chartScale || undefined)}
          // Combo: the quant chart chrome (view-settings popover / metric toggle / small-n caption /
          // chart export) belongs to the Chart tab only; it is suppressed on the Why / Quotes tabs
          // (whose exhibit is the OE card). The combo tab strip owns the viewToggle slot; the
          // composed provenance+base footnote is tab-independent so it shows on every tab.
          // Corner gear (`settingsControlEl`) is view settings only. Cuts stay in the pill row
          // (`filterPillsEl` via the unused plotSwitch slot). On the Drivers tab (`comboOffChart`),
          // the settings popover is replaced by the Heatmap⇄Bars themes-view control when either
          // drivers path can heatmap; it stays null on the Quotes tab, where
          // `comboThemesToggleOffered` is false.
          segmentControl={comboOffChart ? comboThemesControlEl : settingsControlEl}
          plotSwitch={comboOffChart ? null : filterPillsEl}
          metricToggle={
            comboOffChart
              ? null
              : prePostToggleEl
                ? (
                  <>
                    {metricToggleEl}
                    {prePostToggleEl}
                  </>
                )
                : metricToggleEl
          }
          // viewToggle is intentionally NOT passed — the content/view toggle now renders ABOVE the
          // card (`cardViewToggle`), so the PlotCard header keeps only the title + hover actions.
          caption={comboOffChart ? null : plotCaptionEl}
          footnote={plotCardFootnote}
          enableImageExport={
            hasData &&
            (isComboCard
              ? clampedComboTab === 'chart'
              : !isOpenEndCard || (clampedOpenEndTab === 'themes' && hasExportableThemes))
          }
          exportFilename={exportFilename}
        >
          {chartRegionEl}
        </PlotCard>
        }
       </>
      ) : (
        <>
          {segmentControlEl}

          {(plotSwitchEl || metricToggleEl || prePostToggleEl) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {plotSwitchEl}
              {metricToggleEl}
              {prePostToggleEl}
            </div>
          )}

          {chartRegionEl}

          {plotCaptionEl}
          {/* The monadic open-end card renders its OWN methodology note inside the exhibit on this
              inline (report/preview) path, so the generic coverage footer is suppressed for it —
              `!isMonadicOpenEnd` preserves the byte-identical pre-Step-6 behavior now that
              `keepsOwnChrome` is retired. */}
          {!isConvergedDiverging && !isMonadicOpenEnd && (
            <p className="text-xs text-twin-chat-fg-muted">{coverageFooter(r.n, r.total, r)}</p>
          )}
        </>
      )) : null}
    </>
  )

  const nextBody = (
    <>
      {nextSteps.length > 0 && (
        // QS — ONE consolidated next-steps block: a short wrap-up intro, then every move
        // as a lettered a–c list (study chips + focus group + debate + run-more-twins),
        // with exactly one marked (Recommended). Each option is selectable — its letter
        // badge is OUTLINED by default and FILLS with the accent color once chosen (the
        // choice is shown in-card; the echoing user bubble is suppressed in the page
        // render loop). selectedStepKey persists on the message, so the state survives a reload.
        <div className="space-y-2 pt-0.5" data-testid="qs-next-steps">
          <p className="text-[13px] leading-relaxed text-twin-chat-fg-muted" data-testid="qs-next-steps-intro">
            <ComparisonLabelText text={intro} labels={comparisonLabels} />
          </p>
          <div className="flex flex-col gap-1.5">
            {nextSteps.map((step, i) => {
              const selected = selectedStepKey === step.key
              const disabled = step.kind === 'more_twins' && !!confirmMoreBusy
              return (
                <button
                  key={step.key}
                  type="button"
                  onClick={() => handleStepClick(step)}
                  disabled={disabled}
                  data-testid="qs-next-step"
                  data-step-kind={step.kind}
                  data-step-key={step.key}
                  data-selected={selected ? 'true' : 'false'}
                  data-recommended={step.recommended ? 'true' : 'false'}
                  className={cn(
                    'group flex w-full items-start gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors disabled:pointer-events-none disabled:opacity-50',
                    selected
                      ? 'border-primary bg-primary/5'
                      : step.recommended && !selectedStepKey
                        ? 'border-primary'
                        : 'border-twin-chat-divider hover:border-twin-chat-fg/40 hover:bg-twin-chat-bubble/30',
                  )}
                >
                  {/* Selectable letter badge: recommended gets a primary outline only until
                      something is chosen; fill + inverted letter only on the clicked step. */}
                  <span
                    aria-hidden
                    className={cn(
                      'mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium transition-colors',
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : step.recommended && !selectedStepKey
                          ? 'border-primary text-twin-chat-fg-muted'
                          : 'border-twin-chat-divider text-twin-chat-fg-muted group-hover:border-twin-chat-fg/50 group-hover:text-twin-chat-fg',
                    )}
                  >
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-twin-chat-fg">
                      <ComparisonLabelText text={step.label} labels={comparisonLabels} />
                      {step.recommended && (
                        <span className="font-normal text-twin-chat-fg-muted"> (Recommended)</span>
                      )}
                    </span>
                    {step.hint ? (
                      <span className="block text-xs text-twin-chat-fg-muted">
                        <ComparisonLabelText text={step.hint} labels={comparisonLabels} />
                      </span>
                    ) : null}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )

  // Report / preview / tests: one continuous block (unchanged).
  if (!bubbled) {
    return (
      <div className="w-full space-y-4">
        {insightBody}
        {region === 'all' ? nextBody : null}
      </div>
    )
  }

  // Chat: two stacked assistant bubbles — insight first, "what to ask next" second.
  return (
    <div className="space-y-5">
      <div className="animate-[chat-message-in_0.28s_ease-out_both] motion-reduce:animate-none">
        <AssistantActionCard surface="plain">
          <div className="w-full space-y-4">{insightBody}</div>
        </AssistantActionCard>
      </div>
      {hasNextContent && (
        <div className="animate-[chat-message-in_0.28s_ease-out_both] motion-reduce:animate-none">
          <AssistantActionCard surface="plain">
            <div className="w-full space-y-4">{nextBody}</div>
          </AssistantActionCard>
        </div>
      )}
    </div>
  )
}

// QS-2b — adapt a free-text `ask_next` prompt into the {label, send} shape the
// suggestion chips use, so a result with no explicit suggestions still routes its
// follow-up through the SAME handleSuggestion path (segment-aware re-slice OR ask
// the agent). Returns null for an empty/whitespace prompt so the caller can fall
// back to nothing. Pure + exported for unit coverage.
export function askNextSuggestion(askNext: string | undefined | null): { label: string; send: string } | null {
  const text = (askNext ?? '').trim()
  if (!text) return null
  return { label: text, send: text }
}

// Retired next-step lane: segment / "break down by …" chips no longer surface on the
// insight card (chart Compare-segments owns that UX). Keep in lockstep with python
// `_is_segment_chip` (analysis.py) — kind first, then tight cut-intent phrasing only
// (never a bare "break"+"down" pair; that would kill deepen chips like "Break down the objections").
export function isSegmentSuggestionChip(s: {
  label?: string
  send?: string
  hint?: string
  kind?: string
}): boolean {
  const kind = (s.kind || '').trim().toLowerCase()
  if (kind === 'segment') return true
  const text = `${s.label || ''} ${s.send || ''} ${s.hint || ''}`
  if (/\bbreak(?:ing)?\b[\w\s,'-]{0,40}\bdown\s+by\b/i.test(text)) return true
  if (/\b(?:check|test)\s+(?:a\s+|by\s+)?segment\b|\bby\s+segment\b/i.test(text)) return true
  if (/\b(?:split|cut)\s+by\b/i.test(text)) return true
  if (/\b(?:break|split|segment|cut|re-?slice)\b[\w\s,'-]{0,40}\bby\s+age\b/i.test(text)) return true
  return false
}

// QS-26/88 — does a suggestion chip mean "break down by <attribute>", and is that
// attribute available as a client-side segment cut? Returns the candidate's attribute
// key when so (→ drive the dropdown), else undefined (→ ask the agent). Still used by
// handleSuggestion if a legacy chip somehow fires; new cards filter these out first.
const SEG_INTENT_RE = /\b(break(?:ing)?\s*(?:that|this|it|them)?\s*(?:result)?\s*down|segment|split|cut|by)\b/i
// QS-88 — pull the SHORT attribute phrase right after "by " (e.g. "...down by age" →
// "age", "...by age group" → "age group"). Bounded to ≤3 attribute-ish words so it
// stops at a clause boundary instead of swallowing the rest of the string — the old
// pattern ran to end-of-string and produced "age Break down by age", which then only
// matched a literal "age" key by luck and silently failed for "age_group"/"ageBracket".
const SEG_ATTR_RE = /\bby\s+([a-z][a-z-]*(?:\s+[a-z][a-z-]*){0,2})/i
export function segmentTargetOf(
  s: { label: string; send: string },
  candidates: SegmentQuestion[],
): string | undefined {
  // QS-88 — test each field INDEPENDENTLY: concatenating label+send let the "by"
  // capture cross the field gap and pollute the attribute. The first field whose
  // attribute resolves to a candidate wins.
  for (const field of [s.label, s.send]) {
    if (!field || !SEG_INTENT_RE.test(field)) continue
    const want = (SEG_ATTR_RE.exec(field)?.[1] || field).trim()
    const hit = matchSegmentName(want, candidates)
    if (hit) return hit
  }
  return undefined
}

// Resolve the agent's segment_by (e.g. "gender", "age", "income") to one of the
// available demographic candidates, alias-tolerant (ignores case/spacing/underscores).
function matchSegmentName(segmentBy: string | null | undefined, candidates: SegmentQuestion[]): string {
  const want = (segmentBy || '').trim()
  if (!want) return ''
  const norm = (s: string) => s.toLowerCase().replace(/[_\s-]+/g, '')
  const w = norm(want)
  const hit = candidates.find((c) => {
    const n = norm(c.name)
    const t = norm(c.title)
    return n === w || t === w || n.includes(w) || w.includes(n) || t.includes(w)
  })
  return hit ? hit.name : ''
}

